import { Action, createReducer, on } from '@ngrx/store'
import { instanceToInstance, plainToInstance } from 'class-transformer'
import { cloneDeep, isNil, unionBy } from 'lodash'

import { ScopeVersion } from '@app/core/model/scope-version'
import { ThirdPartyCost } from '@app/core/model/third-party-cost.model'
import {
  scopeInitialPaginationState,
  scopeOverviewInitialState,
} from '@app/features/scope-overview/const/scope-overview-initial-state.const'
import { DeliverableMoneyUtils } from '@shared/utils/deliverable-money.utils'

import { CompanyScopeCustomFieldsDefinition } from '@app/features/scope-overview/model/company-scope-custom-fields-definition.model'
import { FeeItemInstance } from '@app/features/scope-overview/model/fee-item.model'
import { Scenario } from '@app/features/scope-overview/model/scenario.model'
import { ScopeCustomFieldValueStructure } from '@app/features/scope-overview/model/scope-custom-field-value-structure.model'
import { ScopeOverviewState } from '@app/features/scope-overview/model/scope-overview.state.model'
import { ScopeSection } from '@app/features/scope-overview/model/scope-section'
import { ScopeOverviewActions } from '@app/features/scope-overview/store/actions/scope-overview.action'
import { mapScenario } from '@app/features/scope-overview/utils/map-scenario-util.const'
import { remapDeliverablesInTradedHierarchy } from '@app/features/scope-overview/utils/remap-deliverables-in-traded-hierarchy.utils'
import { Deliverable } from '@app/features/scoping/models/deliverable.model'
import { ScopeSubmitter } from '@core/model/scope-submitter.model'
import { ScopeTeam } from '@core/model/scope-team.model'
import { ScopeVoter } from '@core/model/scope-voter.model'
import { mapScopeDeliverable } from '../../utils/map-scope-deliverable.utils'
import { mapScopeSection } from '../../utils/map-scope-entity.utils'
import { ScopeConfigurationActions } from '../actions/scope-configuration.actions'
import { ScopePaginatedOverviewActions } from '../actions/scope-paginated-overview.actions'
import { ScopeComponent } from '@app/features/scoping/models/component.model'
import { MoveDeliverablePayload } from '../../model/move-deliverable.payload.model'
import { Money } from '../../model/money.model'
import { DEFAULT_PAGE_SIZE } from '../../service/scope-paginated.service'

function setScopeNotificationEvent(state: ScopeOverviewState, action: any) {
  // Create an instance of the relevant class from the current state's currentScope
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)

  // Extract scopeNotificationProps from action
  const { scopeNotificationProps } = action

  // Iterate over the properties in scopeNotificationProps and update them on the currentScopeInstance
  Object.keys(scopeNotificationProps).forEach((key) => {
    currentScopeInstance[key] = scopeNotificationProps[key]
  })

  return { ...state, currentScope: currentScopeInstance }
}

function getCurrentScopeHandler(state: ScopeOverviewState, { keepCurrentScope = false }) {
  return {
    ...state,
    currentScope: keepCurrentScope
      ? plainToInstance(ScopeVersion, {
          ...state.currentScope,
          deliverables: [],
          sections: [],
        })
      : undefined,
    currentScopeDeliverablesPagination: scopeInitialPaginationState,
    currentScopeSectionsPagination: scopeInitialPaginationState,
    currentScopeSectionDeliverablesPagination: {},
    scenario: undefined,
    loading: { ...state.loading, scope: true },
    loaded: { ...state.loaded, scope: false },
  }
}

function getCurrentScopeByRoleSuccessHandler(state: ScopeOverviewState, { currentScope }) {
  const currentScopeInstance = currentScope
    ? plainToInstance(ScopeVersion, {
        ...currentScope,
        budget: new Money(
          isNil(currentScope.budget?.amount) ? currentScope.budget : currentScope.budget?.amount,
          currentScope.currency
        ),
      })
    : scopeOverviewInitialState.currentScope

  return {
    ...state,
    currentScope: currentScopeInstance,
    configurationHasUpdates: false,
    loading: { ...state.loading, scope: false },
    loaded: { ...state.loaded, scope: true, allFees: false, allThirdPartyCosts: false },
  }
}

function getCurrentScopeSuccessHandler(state: ScopeOverviewState, { currentScope }) {
  const currentScopeInstance = currentScope
    ? plainToInstance(ScopeVersion, {
        ...currentScope,
        calculatedValues: currentScope.calculatedValues || state.currentScope?.calculatedValues,
        budget: new Money(
          isNil(currentScope.budget?.amount) ? currentScope.budget : currentScope.budget?.amount,
          currentScope.currency
        ),
        deliverables: [],
        sections: [],
      })
    : scopeOverviewInitialState.currentScope

  return {
    ...state,
    currentScope: currentScopeInstance,
    configurationHasUpdates: scopeOverviewInitialState.configurationHasUpdates,
    loading: { ...state.loading, scope: false },
    loaded: { ...state.loaded, scope: true, allFees: false, allThirdPartyCosts: false },
    currentScopeDeliverablesPagination: scopeOverviewInitialState.currentScopeDeliverablesPagination,
    currentScopeSectionsPagination: scopeOverviewInitialState.currentScopeSectionsPagination,
    currentScopeSectionDeliverablesPagination: scopeOverviewInitialState.currentScopeSectionDeliverablesPagination,
    displayedDeliverablesCount: scopeOverviewInitialState.displayedDeliverablesCount,
  }
}

function getCurrentScopeValuesHandler(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, scopeValues: true },
    loaded: { ...state.loaded, scopeValues: false },
  }
}

function getCurrentScopeValuesSuccessHandler(state: ScopeOverviewState, { currentScope }) {
  if (state.currentScope?.identity?.id !== currentScope.identity?.id) return state

  const currentScopeInstance = plainToInstance(ScopeVersion, {
    ...state.currentScope,
    calculatedValues: currentScope.calculatedValues,
    margin: currentScope.margin,
  })

  return {
    ...state,
    currentScope: currentScopeInstance,
    loading: { ...state.loading, scopeValues: false },
    loaded: { ...state.loaded, scopeValues: true },
  }
}

function getCurrentScopeValuesFailHandler(state: ScopeOverviewState, error) {
  return {
    ...state,
    error,
    loading: { ...state.loading, scopeValues: false },
    loaded: { ...state.loaded, scopeValues: true },
  }
}

const getScopeDeliverablesLoadingHandler = (loading) => (state: ScopeOverviewState) => ({
  ...state,
  currentScopeDeliverablesPagination: {
    ...state.currentScopeDeliverablesPagination,
    number: loading
      ? state.currentScopeDeliverablesPagination.number + 1
      : state.currentScopeDeliverablesPagination.number,
    loading,
  },
})

const getScopeSectionDeliverablesLoadingHandler =
  (loading) =>
  (state: ScopeOverviewState, { sectionId }) => ({
    ...state,
    currentScopeSectionDeliverablesPagination: {
      ...state.currentScopeSectionDeliverablesPagination,
      [sectionId]: {
        ...scopeInitialPaginationState,
        ...state.currentScopeSectionDeliverablesPagination[sectionId],
        loading,
      },
    },
  })

function getScopeDeliverablesSuccessHandler(state: ScopeOverviewState, { response }) {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)
  if (!currentScopeInstance) return state

  currentScopeInstance.deliverables = [
    ...(currentScopeInstance.deliverables || []),
    ...response.content.map((d) => mapScopeDeliverable(d)),
  ]

  remapDeliverablesInTradedHierarchy(currentScopeInstance)

  return {
    ...state,
    currentScope: currentScopeInstance,
    currentScopeDeliverablesPagination: {
      ...response,
      loading: false,
    },
    displayedDeliverablesCount: response.first ? response.numberOfElements : state.displayedDeliverablesCount,
  }
}

const showNextBufferedDeliverablesHandler = (state: ScopeOverviewState) => {
  const totalLoaded = state.currentScope?.deliverables?.length || 0
  const currentDisplayed = state.displayedDeliverablesCount

  if (currentDisplayed >= totalLoaded) return state

  return {
    ...state,
    displayedDeliverablesCount: Math.min(currentDisplayed + DEFAULT_PAGE_SIZE, totalLoaded),
  }
}

const getScopeSectionsLoadingHandler = (loading) => (state: ScopeOverviewState) => ({
  ...state,
  currentScopeSectionsPagination: {
    ...state.currentScopeSectionsPagination,
    number: loading ? state.currentScopeSectionsPagination.number + 1 : state.currentScopeSectionsPagination.number,
    loading,
  },
})

function getScopeSectionsSuccessHandler(state: ScopeOverviewState, { response }) {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)
  if (!currentScopeInstance) return state

  const mappedSections = response.content.map((sectionData) => DeliverableMoneyUtils.prepareSection(sectionData))
  currentScopeInstance.sections = [...(currentScopeInstance?.sections || []), ...mappedSections.map(mapScopeSection)]

  return {
    ...state,
    currentScope: currentScopeInstance,
    currentScopeSectionsPagination: {
      ...response,
      loading: false,
    },
  }
}

function getScopeSectionDeliverablesSuccessHandler(state: ScopeOverviewState, { sectionId, response }) {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)
  if (!currentScopeInstance) return state

  const allDeliverables = currentScopeInstance.deliverables || []
  const mappedSectionDeliverables = response.content.map((d) => mapScopeDeliverable(d, sectionId))
  const deliverablesWithoutStaleSection =
    response.first === true ? allDeliverables.filter((d) => d.section?.id !== sectionId) : allDeliverables
  currentScopeInstance.deliverables = [...deliverablesWithoutStaleSection, ...mappedSectionDeliverables]

  return {
    ...state,
    currentScope: currentScopeInstance,
    currentScopeSectionDeliverablesPagination: {
      ...state.currentScopeSectionDeliverablesPagination,
      [sectionId]: {
        ...response,
        loading: false,
      },
    },
  }
}

function getCurrentScopeFailHandler(state: ScopeOverviewState, error) {
  return { ...state, error, loading: { ...state.loading, scope: false }, loaded: { ...state.loaded, scope: false } }
}

function getScopeRetainedStats(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, scopeRetainedStats: true },
    loaded: { ...state.loaded, scopeRetainedStats: false },
  }
}

function getScopeRetainedStatsSuccess(state: ScopeOverviewState, { scopeRetainedHourStats }) {
  return {
    ...state,
    scopeRetainedHourStats,
    loading: { ...state.loading, scopeRetainedStats: false },
    loaded: { ...state.loaded, scopeRetainedStats: true },
  }
}

function getScopeRetainedStatsFail(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, scopeRetainedStats: false },
    loaded: { ...state.loaded, scopeRetainedStats: false },
  }
}

function getSowRetainedStats(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, sowRetainedStats: true },
    loaded: { ...state.loaded, sowRetainedStats: false },
  }
}

function getSowRetainedStatsSuccess(state: ScopeOverviewState, { sowRetainedHourStats }) {
  return {
    ...state,
    sowRetainedHourStats,
    loading: { ...state.loading, sowRetainedStats: false },
    loaded: { ...state.loaded, sowRetainedStats: true },
  }
}

function getSowRetainedStatsFail(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, sowRetainedStats: false },
    loaded: { ...state.loaded, sowRetainedStats: false },
  }
}

function getActivityCommentsSuccessHandler(state: ScopeOverviewState, { activities }) {
  return { ...state, activities }
}

function submitCommentSuccessHandler(state: ScopeOverviewState, { updatedComments }) {
  return { ...state, activities: updatedComments }
}

function getThirdPartyCostBalance(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, thirdPartyCostBalance: true },
    loaded: { ...state.loaded, thirdPartyCostBalance: false },
  }
}

function getThirdPartyCostBalanceSuccess(state: ScopeOverviewState, { thirdPartyCostBalance }) {
  return {
    ...state,
    thirdPartyCostBalance,
    loading: { ...state.loading, thirdPartyCostBalance: false },
    loaded: { ...state.loaded, thirdPartyCostBalance: true },
  }
}

function getThirdPartyCostBalanceFail(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, thirdPartyCostBalance: false },
    loaded: { ...state.loaded, thirdPartyCostBalance: false },
  }
}

function changeScopeVersionHandler(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, scope: true },
    loaded: { ...state.loaded, scope: false },
  }
}

function changeScopeVersionSuccess(state: ScopeOverviewState, { currentScope }) {
  const configuration = currentScope.configuration || {}
  let scenario
  if (state.scenario) scenario = mapScenario(instanceToInstance(state.scenario), configuration)

  return {
    ...state,
    loading: { ...state.loading, scope: false },
    loaded: { ...state.loaded, scope: true },
    currentScope,
    scenario,
  }
}

function changeScopeVersionFail(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, scope: false },
    loaded: { ...state.loaded, scope: true },
  }
}

function addScopeSection(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, addScopeSection: true },
    loaded: { ...state.loaded, addScopeSection: false },
  }
}

function addScopeSectionSuccess(state: ScopeOverviewState, action: any): ScopeOverviewState {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)
  const section = plainToInstance(ScopeSection, action.scopeSection)

  if (!currentScopeInstance.sections.find((s) => s.id == section.id)) {
    currentScopeInstance.sections = [...currentScopeInstance.sections, section]
  }

  return {
    ...state,
    currentScope: currentScopeInstance,
    loading: { ...state.loading, addScopeSection: false },
    loaded: { ...state.loaded, addScopeSection: true },
  }
}

function addScopeSectionFail(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, addScopeSection: false },
    loaded: { ...state.loaded, addScopeSection: true },
  }
}

function deleteScopeSection(state: ScopeOverviewState, error) {
  return {
    ...state,
    error,
    loading: { ...state.loading, libraryEntry: false },
    loaded: { ...state.loaded, libraryEntry: true },
  }
}

function deleteScopeSectionSuccess(state: ScopeOverviewState, action: any) {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)

  currentScopeInstance.sections = currentScopeInstance.sections.filter((section) => section.id !== action.sectionId)

  return {
    ...state,
    currentScope: currentScopeInstance,
    loading: { ...state.loading, deleteScopeSection: false },
    loaded: { ...state.loaded, deleteScopeSection: true },
  }
}

function deleteScopeSectionFail(state: ScopeOverviewState, error) {
  return {
    ...state,
    error,
    loading: { ...state.loading, deleteScopeSection: false },
    loaded: { ...state.loaded, deleteScopeSection: true },
  }
}

function archiveScope(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, archiveScopeSection: true },
    loaded: { ...state.loaded, archiveScopeSection: false },
  }
}

function archiveScopeSuccess(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, archiveScopeSection: false },
    loaded: { ...state.loaded, archiveScopeSection: true },
  }
}

function archiveScopeFail(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, archiveScopeSection: false },
    loaded: { ...state.loaded, archiveScopeSection: true },
  }
}

function unarchiveScope(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, unarchiveScopeSection: true },
    loaded: { ...state.loaded, unarchiveScopeSection: false },
  }
}

function unarchiveScopeSuccess(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, unarchiveScopeSection: false },
    loaded: { ...state.loaded, unarchiveScopeSection: true },
  }
}

function unarchiveScopeFail(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, unarchiveScopeSection: false },
    loaded: { ...state.loaded, unarchiveScopeSection: true },
  }
}

function getUpdatedDeliverable(state: ScopeOverviewState, { deliverableId }) {
  return {
    ...state,
    loading: { ...state.loading, deliverable: { ...state.loading.deliverable, [deliverableId]: true } },
    loaded: { ...state.loaded, deliverable: { ...state.loaded.deliverable, [deliverableId]: false } },
  }
}

function getScopeDeliverable(state: ScopeOverviewState, { deliverableId }) {
  return {
    ...state,
    loading: {
      ...state.loading,
      getScopeDeliverable: true,
      deliverable: { ...state.loading.deliverable, [deliverableId]: true },
    },
    loaded: {
      ...state.loaded,
      getScopeDeliverable: false,
      deliverable: { ...state.loaded.deliverable, [deliverableId]: false },
    },
  }
}

function getScopeDeliverableSuccessHandler(state: ScopeOverviewState, { deliverable }: any) {
  if (!state.currentScope) return state

  const deliverables = (state.currentScope.deliverables || []).map((d) =>
    d.id === deliverable.id ? mapScopeDeliverable(deliverable, d.section?.id) : d
  )

  return {
    ...state,
    currentScope: plainToInstance(ScopeVersion, { ...state.currentScope, deliverables }),
    loading: {
      ...state.loading,
      getScopeDeliverable: false,
      deliverable: { ...state.loading.deliverable, [deliverable.id]: false },
    },
    loaded: {
      ...state.loaded,
      getScopeDeliverable: true,
      deliverable: { ...state.loaded.deliverable, [deliverable.id]: true },
    },
  }
}

function getScopeDeliverableFailHandler(state: ScopeOverviewState, { deliverableId }) {
  return {
    ...state,
    loading: {
      ...state.loading,
      getScopeDeliverable: false,
      deliverable: { ...state.loading.deliverable, [deliverableId]: false },
    },
    loaded: {
      ...state.loaded,
      getScopeDeliverable: false,
      deliverable: { ...state.loaded.deliverable, [deliverableId]: false },
    },
  }
}

function getAllTasksHandler(state: ScopeOverviewState) {
  return { ...state, loading: { ...state.loading, allTasks: true }, loaded: { ...state.loaded, allTasks: false } }
}

function getAllTasksSuccessHandler(state: ScopeOverviewState, { tasks }) {
  const deliverables = tasks.map((deliverable) =>
    plainToInstance(Deliverable, { ...deliverable, componentsInitialised: true })
  )

  return {
    ...state,
    currentScope: plainToInstance(ScopeVersion, { ...state.currentScope, deliverables: deliverables }),
    loading: { ...state.loading, allTasks: false },
    loaded: { ...state.loaded, allTasks: true },
  }
}

function getAllTasksFailHandler(state: ScopeOverviewState) {
  return { ...state, loading: { ...state.loading, allTasks: false }, loaded: { ...state.loaded, allTasks: false } }
}

function getAvailableDeliverableTypes(state: ScopeOverviewState) {
  return {
    ...state,
    availableDeliverableTypes: null,
    loading: { ...state.loading, availableDeliverableTypes: true },
    loaded: { ...state.loaded, availableDeliverableTypes: false },
  }
}

function getAvailableDeliverableTypesSuccess(state: ScopeOverviewState, { availableDeliverableTypes }) {
  return {
    ...state,
    availableDeliverableTypes: availableDeliverableTypes,
    loading: { ...state.loading, availableDeliverableTypes: false },
    loaded: { ...state.loaded, availableDeliverableTypes: true },
  }
}

function getAvailableDeliverableTypesFail(state: ScopeOverviewState, error) {
  return {
    ...state,
    error,
    loading: { ...state.loading, availableDeliverableTypes: false },
    loaded: { ...state.loaded, availableDeliverableTypes: true },
  }
}

function addScopeDeliverable(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, addScopeDeliverable: true },
    loaded: { ...state.loaded, addScopeDeliverable: false },
  }
}

function addScopeDeliverableSuccessHandler(state: ScopeOverviewState, action): ScopeOverviewState {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)

  const deliverableInstance = plainToInstance(Deliverable, action.deliverable)
  deliverableInstance.componentsInitialised = true
  const deliverables = currentScopeInstance.deliverables || []

  if (!deliverables.find((del) => del.id == deliverableInstance.id)) deliverables.push(deliverableInstance)

  currentScopeInstance.deliverables = deliverables
  currentScopeInstance.calculatedValues = null

  return {
    ...state,
    currentScope: currentScopeInstance,
    displayedDeliverablesCount: state.displayedDeliverablesCount + 1,
    loading: { ...state.loading, addScopeDeliverable: false },
    loaded: { ...state.loaded, addScopeDeliverable: true },
  }
}

function addScopeDeliverableFailHandler(state: ScopeOverviewState, error) {
  return {
    ...state,
    error,
    loading: { ...state.loading, addScopeDeliverable: false },
    loaded: { ...state.loaded, addScopeDeliverable: true },
  }
}

function addScopeDeliverablesSuccessHandler(state: ScopeOverviewState, action): ScopeOverviewState {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)

  const deliverables = currentScopeInstance.deliverables
  action.deliverables.forEach((deliverable) => {
    const deliverableInstance = plainToInstance(Deliverable, deliverable)
    deliverableInstance.componentsInitialised = true

    if (!deliverables.find((del) => del.id == deliverableInstance.id)) deliverables.push(deliverableInstance)
  })

  currentScopeInstance.deliverables = deliverables
  currentScopeInstance.calculatedValues = null

  return {
    ...state,
    currentScope: currentScopeInstance,
    loading: { ...state.loading, addScopeDeliverable: false },
    loaded: { ...state.loaded, addScopeDeliverable: true },
  }
}

function duplicateScopeDeliverable(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, duplicateDeliverable: true },
    loaded: { ...state.loaded, duplicateDeliverable: false },
  }
}

function moveComponentToDeliverable(state: ScopeOverviewState, action) {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)
  const deliverable = currentScopeInstance.deliverables.find((d) =>
    d.components?.find((c) => c.id == action.componentId)
  )
  deliverable.components = deliverable.components.filter((c) => c.id != action.componentId)
  let index = 0
  deliverable.components
    .sort((c1, c2) => c1.order - c2.order)
    .forEach((c) => {
      c.order = index
      index++
    })
  return {
    ...state,
    currentScope: currentScopeInstance,
    loading: { ...state.loading, moveComponentToDeliverable: true },
    loaded: { ...state.loaded, moveComponentToDeliverable: false },
  }
}

function duplicateScopeDeliverableSuccessHandler(state: ScopeOverviewState, action): ScopeOverviewState {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)
  const deliverableInstance = plainToInstance(Deliverable, action.deliverable)
  deliverableInstance.componentsInitialised = true
  const deliverables = currentScopeInstance.deliverables

  if (!deliverables.find((del) => del.id == deliverableInstance.id)) deliverables.push(deliverableInstance)

  currentScopeInstance.deliverables = deliverables
  currentScopeInstance.calculatedValues = null

  return {
    ...state,
    currentScope: currentScopeInstance,
    filteredDeliverables: state.filteredDeliverables?.length
      ? [...state.filteredDeliverables, deliverableInstance]
      : undefined,
    loading: { ...state.loading, duplicateDeliverable: false },
    loaded: { ...state.loaded, duplicateDeliverable: true },
  }
}

function duplicateScopeDeliverableFailHandler(state: ScopeOverviewState, error) {
  return {
    ...state,
    error,
    loading: { ...state.loading, duplicateDeliverable: false },
    loaded: { ...state.loaded, duplicateDeliverable: true },
  }
}

function moveComponentToDeliverableFailHandler(state: ScopeOverviewState, error) {
  return {
    ...state,
    error,
    loading: { ...state.loading, moveComponentToDeliverable: false },
    loaded: { ...state.loaded, moveComponentToDeliverable: true },
  }
}

function moveComponentToDeliverableSuccessHandler(state: ScopeOverviewState, { deliverable }: any): ScopeOverviewState {
  const clonedDel = cloneDeep(deliverable)
  clonedDel.componentsInitialised = true
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)
  currentScopeInstance.deliverables = [
    ...currentScopeInstance.deliverables.filter((del) => del.id != clonedDel.id),
    clonedDel,
  ]
  return {
    ...state,
    currentScope: currentScopeInstance,
    loading: { ...state.loading, updateDeliverable: false },
    loaded: { ...state.loaded, updateDeliverable: true },
  }
}

function updateScopeDeliverable(state: ScopeOverviewState, action) {
  const deliverable = action.deliverable
  let currentScopeInstance = state.currentScope

  if (!deliverable || !currentScopeInstance) return state

  if (!action.pessimistic) {
    currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)
    const index = currentScopeInstance.deliverables.findIndex((d) => d.id == deliverable.id)
    if (!isNil(index)) currentScopeInstance.deliverables[index] = deliverable
  }

  return {
    ...state,
    currentScope: currentScopeInstance,
    loading: {
      ...state.loading,
      deliverable: { ...state.loading.deliverable, [deliverable.id]: true },
    },
    loaded: {
      ...state.loaded,
      deliverable: { ...state.loaded.deliverable, [deliverable.id]: false },
    },
  }
}

function updateScopeDeliverableSuccessHandler(state: ScopeOverviewState, { deliverable }: any): ScopeOverviewState {
  const clonedDel = cloneDeep(deliverable)
  clonedDel.componentsInitialised = !!deliverable.components
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)
  currentScopeInstance.deliverables = currentScopeInstance.deliverables.map((d) =>
    d.id === clonedDel.id ? clonedDel : d
  )

  return {
    ...state,
    currentScope: currentScopeInstance,
    loading: {
      ...state.loading,
      updateDeliverable: false,
      deliverable: { ...state.loading.deliverable, [deliverable.id]: false },
    },
    loaded: {
      ...state.loaded,
      updateDeliverable: true,
      deliverable: { ...state.loaded.deliverable, [deliverable.id]: true },
    },
  }
}

function updateScopeDeliverableFailHandler(
  state: ScopeOverviewState,
  { deliverableId, error }: { deliverableId: number; error?: string }
): ScopeOverviewState {
  return {
    ...state,
    error,
    loading: {
      ...state.loading,
      updateDeliverable: false,
      deliverable: { ...state.loading.deliverable, [deliverableId]: false },
    },
    loaded: {
      ...state.loaded,
      updateDeliverable: true,
      deliverable: { ...state.loaded.deliverable, [deliverableId]: true },
    },
  }
}

function clearScopeTaskRoles(state: ScopeOverviewState, action) {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)

  const index = currentScopeInstance.deliverables.findIndex((d) => d.id == action.taskId)
  currentScopeInstance.deliverables[index].components[0].departments.forEach((dep) => {
    dep.roles.forEach((r) => (r.rateCardMinutes = 0))
  })

  return {
    ...state,
    currentScope: currentScopeInstance,
    loading: { ...state.loading, updateDeliverable: true },
    loaded: { ...state.loaded, updateDeliverable: false },
  }
}

function clearScopeTaskRolesSuccessHandler(state: ScopeOverviewState): ScopeOverviewState {
  return {
    ...state,
    loading: { ...state.loading, updateDeliverable: false },
    loaded: { ...state.loaded, updateDeliverable: true },
  }
}

function clearScopeTaskRolesFailHandler(state: ScopeOverviewState, error) {
  return {
    ...state,
    error,
    loading: { ...state.loading, updateDeliverable: false },
    loaded: { ...state.loaded, updateDeliverable: true },
  }
}

function updateScopeSection(state: ScopeOverviewState, action) {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)

  const index = currentScopeInstance.sections.findIndex((s) => s.id == action.section.id)
  currentScopeInstance.sections[index] = action.section

  return {
    ...state,
    currentScope: currentScopeInstance,
    loading: { ...state.loading, updateSection: true },
    loaded: { ...state.loaded, updateSection: false },
  }
}

function updateScopeSectionSuccessHandler(state: ScopeOverviewState): ScopeOverviewState {
  return {
    ...state,
    loading: { ...state.loading, updateSection: false },
    loaded: { ...state.loaded, updateSection: true },
  }
}

function updateScopeSectionFailHandler(state: ScopeOverviewState, error) {
  return {
    ...state,
    error,
    loading: { ...state.loading, updateSection: false },
    loaded: { ...state.loaded, updateSection: true },
  }
}

function updateScopeComponent(state: ScopeOverviewState, { deliverableId }) {
  return {
    ...state,
    loading: {
      ...state.loading,
      updateDeliverable: true,
      deliverable: { ...state.loading.deliverable, [deliverableId]: true },
    },
    loaded: {
      ...state.loaded,
      updateDeliverable: false,
      deliverable: { ...state.loaded.deliverable, [deliverableId]: false },
    },
  }
}

function updateScopeComponentSuccessHandler(state: ScopeOverviewState, action): ScopeOverviewState {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)

  const deliverable = currentScopeInstance.deliverables.find((d) => d.id === action.deliverableId)
  if (deliverable && deliverable.components) {
    const index = deliverable.components.findIndex((c) => c.id == action.component.id)
    deliverable.components[index] = action.component
  }
  if (action.deliverable) {
    const deliverable = currentScopeInstance.deliverables.find((d) => d.id == action.deliverable.id)
    deliverable.components = action.deliverable.components
  }

  return {
    ...state,
    currentScope: currentScopeInstance,
    loading: {
      ...state.loading,
      updateDeliverable: false,
      deliverable: { ...state.loading.deliverable, [action.deliverableId]: false },
    },
    loaded: {
      ...state.loaded,
      updateDeliverable: true,
      deliverable: { ...state.loaded.deliverable, [action.deliverableId]: true },
    },
  }
}

function updateScopeComponentFailHandler(
  state: ScopeOverviewState,
  { deliverableId, error }: { deliverableId: number; error?: string }
) {
  return {
    ...state,
    error,
    loading: {
      ...state.loading,
      updateDeliverable: false,
      deliverable: { ...state.loading.deliverable, [deliverableId]: false },
    },
    loaded: {
      ...state.loaded,
      updateDeliverable: true,
      deliverable: { ...state.loaded.deliverable, [deliverableId]: true },
    },
  }
}

function updateDeliverableSectionHandler(state: ScopeOverviewState, action) {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)

  const deliverable = currentScopeInstance.deliverables.find((d) => d.id === action.section.deliverableId)
  if (deliverable && deliverable.sections) {
    const index = deliverable.sections.findIndex((c) => c.id == action.section.id)
    deliverable.sections[index] = action.section
  }

  return {
    ...state,
    currentScope: currentScopeInstance,
    loading: { ...state.loading, updateDeliverable: true },
    loaded: { ...state.loaded, updateDeliverable: false },
  }
}

function updateDeliverableSectionSuccessHandler(state: ScopeOverviewState): ScopeOverviewState {
  return {
    ...state,
    loading: { ...state.loading, updateDeliverable: false },
    loaded: { ...state.loaded, updateDeliverable: true },
  }
}

function updateDeliverableSectionFailHandler(state: ScopeOverviewState, error) {
  return {
    ...state,
    error,
    loading: { ...state.loading, updateDeliverable: false },
    loaded: { ...state.loaded, updateDeliverable: true },
  }
}

function overrideScopeComponentSuccessHandler(state: ScopeOverviewState, action): ScopeOverviewState {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)

  const deliverable = currentScopeInstance.deliverables.find((del) => del.id == action.component.deliverable.id)
  if (deliverable) {
    const component = deliverable.components.find((com) => com.id == action.component.id)
    if (component) {
      component.comments = action.component.comments
      component.quantity = action.component.quantity
    }
  }

  return { ...state, currentScope: currentScopeInstance }
}

function updateTpcHandler(state: ScopeOverviewState, action) {
  return {
    ...state,
    loading: { ...state.loading, deliverable: { ...state.loading.deliverable, [action.deliverable.id]: true } },
    loaded: { ...state.loaded, deliverable: { ...state.loaded.deliverable, [action.deliverable.id]: false } },
  }
}

function updateTpcFailHandler(state: ScopeOverviewState, action) {
  return {
    ...state,
    loading: { ...state.loading, deliverable: { ...state.loading.deliverable, [action.deliverableId]: false } },
    loaded: { ...state.loaded, deliverable: { ...state.loaded.deliverable, [action.deliverableId]: true } },
  }
}

function updateDeliverableTpcSuccessHandler(state: ScopeOverviewState, action): ScopeOverviewState {
  const currentScopeInstance = plainToInstance(ScopeVersion, {
    ...state.currentScope,
    deliverables: state.currentScope.deliverables.map((deliverable) =>
      deliverable.id == action.deliverable.id
        ? {
            ...deliverable,
            thirdPartyCosts: deliverable.thirdPartyCosts?.map((tpc) =>
              tpc.id == action.thirdPartyCost.id ? action.thirdPartyCost : tpc
            ),
          }
        : deliverable
    ),
  })

  return {
    ...state,
    currentScope: currentScopeInstance,
    loading: { ...state.loading, deliverable: { ...state.loading.deliverable, [action.deliverable.id]: false } },
    loaded: { ...state.loaded, deliverable: { ...state.loaded.deliverable, [action.deliverable.id]: true } },
  }
}

function updateComponentTpcSuccessHandler(state: ScopeOverviewState, action): ScopeOverviewState {
  const currentScopeInstance = plainToInstance(ScopeVersion, {
    ...state.currentScope,
    deliverables: state.currentScope.deliverables.map((deliverable) =>
      deliverable.id == action.deliverable.id
        ? {
            ...deliverable,
            components: (deliverable.components || []).map((component) =>
              component.id === action.component.id
                ? {
                    ...component,
                    thirdPartyCosts: component.thirdPartyCosts?.map((tpc) =>
                      tpc.id == action.thirdPartyCost.id ? action.thirdPartyCost : tpc
                    ),
                  }
                : component
            ),
          }
        : deliverable
    ),
  })

  return {
    ...state,
    currentScope: currentScopeInstance,
    loading: { ...state.loading, deliverable: { ...state.loading.deliverable, [action.deliverable.id]: false } },
    loaded: { ...state.loaded, deliverable: { ...state.loaded.deliverable, [action.deliverable.id]: true } },
  }
}

function updateDeliverableSectionTpcSuccessHandler(state: ScopeOverviewState, action): ScopeOverviewState {
  const currentScopeInstance = plainToInstance(ScopeVersion, {
    ...state.currentScope,
    deliverables: state.currentScope.deliverables.map((deliverable) =>
      deliverable.id == action.deliverable.id
        ? {
            ...deliverable,
            sections: deliverable.sections.map((section) =>
              section.id === action.section.id
                ? {
                    ...section,
                    thirdPartyCosts: section.thirdPartyCosts?.map((tpc) =>
                      tpc.id == action.thirdPartyCost.id ? action.thirdPartyCost : tpc
                    ),
                  }
                : section
            ),
          }
        : deliverable
    ),
  })

  return {
    ...state,
    currentScope: currentScopeInstance,
    loading: { ...state.loading, deliverable: { ...state.loading.deliverable, [action.deliverable.id]: false } },
    loaded: { ...state.loaded, deliverable: { ...state.loaded.deliverable, [action.deliverable.id]: true } },
  }
}

function updateRoleHandler(state: ScopeOverviewState, action): ScopeOverviewState {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)

  const deliverable = currentScopeInstance.deliverables.find((del) => del.id == action.taskId)
  if (deliverable) {
    const department = deliverable.components[0].departments.find((dep) => dep.id == action.role.department.id)
    if (department) {
      const role = department.roles.find((role) => role.id == action.role.id)
      if (role) {
        Object.assign(role, action.role)
      }
    }
  }

  return { ...state, currentScope: currentScopeInstance }
}

function overrideRoleHandler(state: ScopeOverviewState, action): ScopeOverviewState {
  return {
    ...state,
    loading: {
      ...state.loading,
      updateDeliverable: false,
      deliverable: { ...state.loading.deliverable, [action.component.deliverable?.id]: true },
    },
    loaded: {
      ...state.loaded,
      updateDeliverable: true,
      deliverable: { ...state.loaded.deliverable, [action.component.deliverable?.id]: false },
    },
  }
}

function overrideRoleSuccessHandler(state: ScopeOverviewState, action): ScopeOverviewState {
  return {
    ...state,
    loading: {
      ...state.loading,
      updateDeliverable: false,
      deliverable: { ...state.loading.deliverable, [action.component.deliverable?.id]: false },
    },
    loaded: {
      ...state.loaded,
      updateDeliverable: true,
      deliverable: { ...state.loaded.deliverable, [action.component.deliverable?.id]: true },
    },
  }
}

function overrideRoleFailHandler(
  state: ScopeOverviewState,
  { component, error }: { component: ScopeComponent; error?: string }
): ScopeOverviewState {
  return {
    ...state,
    error,
    loading: {
      ...state.loading,
      updateDeliverable: false,
      deliverable: { ...state.loading.deliverable, [component.deliverable?.id]: false },
    },
    loaded: {
      ...state.loaded,
      updateDeliverable: true,
      deliverable: { ...state.loaded.deliverable, [component.deliverable?.id]: true },
    },
  }
}
function createScopeTpcSuccessHandler(state: ScopeOverviewState, action): ScopeOverviewState {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)
  currentScopeInstance.thirdPartyCosts.push(plainToInstance(ThirdPartyCost, action.thirdPartyCost))

  return { ...state, currentScope: currentScopeInstance }
}

function updateScopeTpcSuccessHandler(state: ScopeOverviewState, action): ScopeOverviewState {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)
  const tpc = currentScopeInstance.thirdPartyCosts.find((t) => t.id == action.thirdPartyCost.id)
  if (tpc) {
    Object.assign(tpc, plainToInstance(ThirdPartyCost, action.thirdPartyCost))
  }

  return { ...state, currentScope: currentScopeInstance }
}

function deleteScopeTpcSuccessHandler(state: ScopeOverviewState, action): ScopeOverviewState {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)
  const thirdPartyCosts = currentScopeInstance.thirdPartyCosts.filter((t) => t.id != action.thirdPartyCost.id)

  return { ...state, currentScope: plainToInstance(ScopeVersion, { ...currentScopeInstance, thirdPartyCosts }) }
}

function deleteDeliverable(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, deleteDeliverable: true },
    loaded: { ...state.loaded, deleteDeliverable: false },
  }
}

function deleteDeliverableSuccess(state: ScopeOverviewState, action) {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)

  currentScopeInstance.deliverables = currentScopeInstance.deliverables.filter(
    (deliverable) => deliverable.id !== action.deliverableId
  )
  currentScopeInstance.calculatedValues = null

  return {
    ...state,
    currentScope: currentScopeInstance,
    filteredDeliverables: state.filteredDeliverables?.filter((deliverable) => deliverable.id !== action.deliverableId),
    loading: { ...state.loading, deleteDeliverable: false },
    loaded: { ...state.loaded, deleteDeliverable: true },
  }
}

function deleteDeliverableFail(state: ScopeOverviewState, error) {
  return {
    ...state,
    error,
    loading: { ...state.loading, deleteDeliverable: false },
    loaded: { ...state.loaded, deleteDeliverable: true },
  }
}

function deleteComponent(state: ScopeOverviewState, { deliverableId }) {
  return {
    ...state,
    loading: {
      ...state.loading,
      deleteComponent: true,
      deliverable: { ...state.loading.deliverable, [deliverableId]: true },
    },
    loaded: {
      ...state.loaded,
      deleteComponent: false,
      deliverable: { ...state.loaded.deliverable, [deliverableId]: false },
    },
  }
}

function deleteComponentSuccess(state: ScopeOverviewState, action) {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)
  const deliverable = currentScopeInstance.deliverables.find((d) => d.id === action.deliverableId)
  if (deliverable && deliverable.components) {
    deliverable.components = deliverable.components.filter((component) => component.id !== action.componentId)
  }

  return {
    ...state,
    currentScope: currentScopeInstance,
    loading: {
      ...state.loading,
      deleteComponent: false,
      deliverable: { ...state.loading.deliverable, [action.deliverableId]: false },
    },
    loaded: {
      ...state.loaded,
      deleteComponent: true,
      deliverable: { ...state.loaded.deliverable, [action.deliverableId]: true },
    },
  }
}

function deleteComponentFail(
  state: ScopeOverviewState,
  { deliverableId, error }: { deliverableId: number; error?: string }
) {
  return {
    ...state,
    error,
    loading: {
      ...state.loading,
      deleteComponent: false,
      deliverable: { ...state.loading.deliverable, [deliverableId]: false },
    },
    loaded: {
      ...state.loaded,
      deleteComponent: true,
      deliverable: { ...state.loaded.deliverable, [deliverableId]: true },
    },
  }
}

function createLibraryTemplate(state: ScopeOverviewState) {
  return {
    ...state,
    deliverableLibraryTemplate: undefined,
    loading: { ...state.loading, libraryTemplate: true },
    loaded: { ...state.loaded, libraryTemplate: false },
  }
}

function createLibraryTemplateSuccess(state: ScopeOverviewState, { template }: any) {
  return {
    ...state,
    deliverableLibraryTemplate: template,
    loading: { ...state.loading, libraryTemplate: false },
    loaded: { ...state.loaded, libraryTemplate: true },
  }
}

function createLibraryTemplateFail(state: ScopeOverviewState, error) {
  return {
    ...state,
    error,
    loading: { ...state.loading, libraryTemplate: false },
    loaded: { ...state.loaded, libraryTemplate: true },
  }
}

function createLibraryEntry(state: ScopeOverviewState) {
  return {
    ...state,
    deliverableLibraryEntry: undefined,
    loading: { ...state.loading, libraryEntry: true },
    loaded: { ...state.loaded, libraryEntry: false },
  }
}

function createLibraryEntrySuccess(state: ScopeOverviewState, { updateEntry }: any) {
  return {
    ...state,
    deliverableLibraryEntry: updateEntry,
    loading: { ...state.loading, libraryEntry: false },
    loaded: { ...state.loaded, libraryEntry: true },
  }
}

function createLibraryEntryFail(state: ScopeOverviewState, error) {
  return {
    ...state,
    error,
    loading: { ...state.loading, libraryEntry: false },
    loaded: { ...state.loaded, libraryEntry: true },
  }
}

function getOutputTemplate(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, outputTemplate: true },
    loaded: { ...state.loaded, outputTemplate: false },
  }
}

function getOutputTemplateSuccess(state: ScopeOverviewState, { template }: any) {
  return {
    ...state,
    outputTemplate: template,
    loading: { ...state.loading, outputTemplate: false },
    loaded: { ...state.loaded, outputTemplate: true },
  }
}

function getOutputTemplateFail(state: ScopeOverviewState, error) {
  return {
    ...state,
    error,
    loading: { ...state.loading, outputTemplate: false },
    loaded: { ...state.loaded, outputTemplate: true },
  }
}

function updateTemplateFail(state: ScopeOverviewState, error) {
  return { ...state, error }
}

function previewScopeDocx(state: ScopeOverviewState) {
  return { ...state, docxPreview: undefined }
}

function previewScopeDocxSuccess(state: ScopeOverviewState, { preview }: any) {
  return { ...state, docxPreview: preview }
}

function previewScopeDocxFail(state: ScopeOverviewState, error) {
  return { ...state, previewError: error }
}

function getSecondParty(state: ScopeOverviewState) {
  return {
    ...state,
    secondParty: null,
    loading: { ...state.loading, secondParty: true },
    loaded: { ...state.loaded, secondParty: false },
  }
}

function getSecondPartySuccess(state: ScopeOverviewState, { secondParty }: any) {
  return {
    ...state,
    secondParty: secondParty,
    loading: { ...state.loading, secondParty: false },
    loaded: { ...state.loaded, secondParty: true },
  }
}

function getSecondPartyFail(state: ScopeOverviewState, error) {
  return {
    ...state,
    error,
    loading: { ...state.loading, secondParty: false },
    loaded: { ...state.loaded, secondParty: true },
  }
}

function getOfficeLocation(state: ScopeOverviewState) {
  return {
    ...state,
    officeLocation: null,
    loading: { ...state.loading, officeLocation: true },
    loaded: { ...state.loaded, officeLocation: false },
  }
}

function getOfficeLocationSuccess(state: ScopeOverviewState, { officeLocation }: any) {
  return {
    ...state,
    officeLocation: officeLocation,
    loading: { ...state.loading, officeLocation: false },
    loaded: { ...state.loaded, officeLocation: true },
  }
}

function getOfficeLocationFail(state: ScopeOverviewState, error) {
  return {
    ...state,
    error,
    loading: { ...state.loading, officeLocation: false },
    loaded: { ...state.loaded, officeLocation: true },
  }
}

function getSowRetainedTeamStats(state: ScopeOverviewState) {
  return {
    ...state,
    sowRetainedTeamStats: null,
    loading: { ...state.loading, sowRetainedTeamStats: true },
    loaded: { ...state.loaded, sowRetainedTeamStats: false },
  }
}

function getSowRetainedTeamStatsSuccess(state: ScopeOverviewState, { sowRetainedTeamStats }: any) {
  return {
    ...state,
    sowRetainedTeamStats,
    loading: { ...state.loading, sowRetainedTeamStats: false },
    loaded: { ...state.loaded, sowRetainedTeamStats: true },
  }
}

function getSowRetainedTeamStatsFail(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, sowRetainedTeamStats: false },
    loaded: { ...state.loaded, sowRetainedTeamStats: false },
  }
}

function getScopeCustomFieldsDefinitionOutdated(state: ScopeOverviewState) {
  return { ...state, customFieldsOutdated: null }
}

function getScopeCustomFieldsDefinitionOutdatedSuccess(state: ScopeOverviewState, { outdated }: any) {
  return { ...state, customFieldsOutdated: outdated }
}

function getScopeCustomFieldsDefinitionOutdatedFail(state: ScopeOverviewState) {
  return { ...state }
}

function getAllRateCardsForScopeCreate(state: ScopeOverviewState) {
  return {
    ...state,
    rateCards: null,
    loading: { ...state.loading, rateCards: true },
    loaded: { ...state.loaded, rateCards: false },
  }
}

function getAllRateCardsForScopeCreateSuccess(state: ScopeOverviewState, { rateCards }: any) {
  return {
    ...state,
    rateCards,
    loading: { ...state.loading, rateCards: false },
    loaded: { ...state.loaded, rateCards: true },
  }
}

function getAllRateCardsForScopeCreateFail(state: ScopeOverviewState) {
  return { ...state, loading: { ...state.loading, rateCards: false }, loaded: { ...state.loaded, rateCards: false } }
}

function getDefaultRateCard(state: ScopeOverviewState) {
  return {
    ...state,
    defaultRateCard: null,
    loading: { ...state.loading, defaultRateCard: true },
    loaded: { ...state.loaded, defaultRateCard: false },
  }
}

function getDefaultRateCardSuccess(state: ScopeOverviewState, { defaultRateCard }: any) {
  return {
    ...state,
    defaultRateCard,
    loading: { ...state.loading, defaultRateCard: false },
    loaded: { ...state.loaded, defaultRateCard: true },
  }
}

function getDefaultRateCardFail(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, defaultRateCard: false },
    loaded: { ...state.loaded, defaultRateCard: false },
  }
}

function updateScope(state: ScopeOverviewState, action) {
  return {
    ...state,
    currentScope: action.scope,
    loading: { ...state.loading, updateScopeDetails: true },
    loaded: { ...state.loaded, updateScopeDetails: false },
  }
}

function updateScopeSuccessHandler(state: ScopeOverviewState, action): ScopeOverviewState {
  if (action.scope.isScopeByRole()) {
    action.scope.deliverables
      .filter((d) => !d.section)
      .forEach((deliverable) => {
        deliverable.departments?.forEach((d) => {
          d.roles.forEach((r) => (r.fte = r.getFtePercentage(action.scope.identity.fteValue)))
        })
      })
  }
  return {
    ...state,
    currentScope: action.scope,
    loading: { ...state.loading, updateScopeDetails: false },
    loaded: { ...state.loaded, updateScopeDetails: true },
  }
}

function updateScopeFailHandler(state: ScopeOverviewState, error) {
  return {
    ...state,
    error,
    loading: { ...state.loading, updateScopeDetails: false },
    loaded: { ...state.loaded, updateScopeDetails: true },
  }
}

function refreshOutdatedCustomFields(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, refreshOutdatedCustomFields: true },
    loaded: { ...state.loaded, refreshOutdatedCustomFields: false },
  }
}

function refreshOutdatedCustomFieldsSuccessHandler(state: ScopeOverviewState, action): ScopeOverviewState {
  return {
    ...state,
    currentScope: plainToInstance(ScopeVersion, {
      ...state.currentScope,
      companyScopeCustomFieldsDefinition: plainToInstance(
        CompanyScopeCustomFieldsDefinition,
        action.updatedScopeVersionCustomFields.companyScopeVersionCustomFieldsDefinition
      ),
      scopeVersionCustomFieldValueStructure: plainToInstance(
        ScopeCustomFieldValueStructure,
        action.updatedScopeVersionCustomFields.scopeVersionCustomFieldValueStructure
      ),
    }),
    customFieldsOutdated: false,
    loading: { ...state.loading, refreshOutdatedCustomFields: false },
    loaded: { ...state.loaded, refreshOutdatedCustomFields: true },
  }
}

function refreshOutdatedCustomFieldsFailHandler(state: ScopeOverviewState, error) {
  return {
    ...state,
    error,
    loading: { ...state.loading, refreshOutdatedCustomFields: false },
    loaded: { ...state.loaded, refreshOutdatedCustomFields: true },
  }
}

function addScopeCollaborator(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, teamMemberUpdate: true },
    loaded: { ...state.loaded, teamMemberUpdate: false },
  }
}

function addScopeCollaboratorSuccessHandler(state: ScopeOverviewState, action) {
  const collaborators = unionBy(
    state.currentScope.collaborators,
    action.collaborators.map((c) => plainToInstance(ScopeSubmitter, { user: c.user })),
    'user.id'
  )
  const teamCollaborators = unionBy(state.currentScope.identity.team.collaborators, action.collaborators, 'id')

  return {
    ...state,
    currentScope: plainToInstance(ScopeVersion, {
      ...state.currentScope,
      collaborators: collaborators,
      identity: {
        ...state.currentScope.identity,
        team: plainToInstance(ScopeTeam, { ...state.currentScope.identity.team, collaborators: teamCollaborators }),
      },
    }),
    loading: { ...state.loading, updateScopeDetails: false },
    loaded: { ...state.loaded, updateScopeDetails: true },
  }
}

function addScopeReviewer(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, teamMemberUpdate: true },
    loaded: { ...state.loaded, teamMemberUpdate: false },
  }
}

function addScopeReviewerSuccessHandler(state: ScopeOverviewState, action) {
  const reviewers = unionBy(
    state.currentScope.reviewers,
    action.reviewers.map((c) => plainToInstance(ScopeSubmitter, { user: c.user })),
    'user.id'
  )
  const teamReviewers = unionBy(state.currentScope.identity.team.reviewers, action.reviewers, 'id')

  return {
    ...state,
    currentScope: plainToInstance(ScopeVersion, {
      ...state.currentScope,
      reviewers: reviewers,
      identity: {
        ...state.currentScope.identity,
        team: plainToInstance(ScopeTeam, { ...state.currentScope.identity.team, reviewers: teamReviewers }),
      },
    }),
    loading: { ...state.loading, teamMemberUpdate: true },
    loaded: { ...state.loaded, teamMemberUpdate: false },
  }
}

function addScopeApprover(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, teamMemberUpdate: true },
    loaded: { ...state.loaded, teamMemberUpdate: false },
  }
}

function addScopeApproverSuccessHandler(state: ScopeOverviewState, action) {
  const approvers = unionBy(
    state.currentScope.approvers,
    action.approvers.map((c) => plainToInstance(ScopeVoter, { voter: c.user })),
    'voter.id'
  )
  const teamApprovers = unionBy(state.currentScope.identity.team.approvers, action.approvers, 'id')

  return {
    ...state,
    currentScope: plainToInstance(ScopeVersion, {
      ...state.currentScope,
      approvers: approvers,
      identity: {
        ...state.currentScope.identity,
        team: plainToInstance(ScopeTeam, { ...state.currentScope.identity.team, approvers: teamApprovers }),
      },
    }),
    loading: { ...state.loading, teamMemberUpdate: true },
    loaded: { ...state.loaded, teamMemberUpdate: false },
  }
}

function addScopeTrafficker(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, teamMemberUpdate: true },
    loaded: { ...state.loaded, teamMemberUpdate: false },
  }
}

function addScopeTraffickerSuccessHandler(state: ScopeOverviewState, action) {
  const traffickers = unionBy(state.currentScope.identity.team.traffickers, action.traffickers, 'id')

  return {
    ...state,
    currentScope: plainToInstance(ScopeVersion, {
      ...state.currentScope,
      identity: {
        ...state.currentScope.identity,
        team: plainToInstance(ScopeTeam, { ...state.currentScope.identity.team, traffickers: traffickers }),
      },
    }),
    loading: { ...state.loading, teamMemberUpdate: true },
    loaded: { ...state.loaded, teamMemberUpdate: false },
  }
}

function updateTeamMember(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, teamMemberUpdate: true },
    loaded: { ...state.loaded, teamMemberUpdate: false },
  }
}

function updateTeamMemberSuccessHandler(state: ScopeOverviewState, action) {
  let team
  if (action.member?.type == 'COLLABORATOR') {
    team = plainToInstance(ScopeTeam, {
      ...state.currentScope.identity.team,
      collaborators: state.currentScope.identity.team.collaborators.map((c) =>
        c.id == action.member.id ? action.member : c
      ),
    })
  } else if (action.member?.type == 'REVIEWER') {
    team = plainToInstance(ScopeTeam, {
      ...state.currentScope.identity.team,
      reviewers: state.currentScope.identity.team.reviewers.map((c) => (c.id == action.member.id ? action.member : c)),
    })
  } else if (action.member?.type == 'APPROVER') {
    team = plainToInstance(ScopeTeam, {
      ...state.currentScope.identity.team,
      approvers: state.currentScope.identity.team.approvers.map((c) => (c.id == action.member.id ? action.member : c)),
    })
  } else if (action.member?.type == 'TRAFFICKER') {
    team = plainToInstance(ScopeTeam, {
      ...state.currentScope.identity.team,
      traffickers: state.currentScope.identity.team.traffickers.map((c) =>
        c.id == action.member.id ? action.member : c
      ),
    })
  }

  return {
    ...state,
    currentScope: plainToInstance(ScopeVersion, {
      ...state.currentScope,
      identity: { ...state.currentScope.identity, team: team },
    }),
    loading: { ...state.loading, teamMemberUpdate: true },
    loaded: { ...state.loaded, teamMemberUpdate: false },
  }
}

function removeScopeCollaborator(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, teamMemberUpdate: true },
    loaded: { ...state.loaded, teamMemberUpdate: false },
  }
}

function removeScopeCollaboratorSuccessHandler(state: ScopeOverviewState, action) {
  return {
    ...state,
    currentScope: plainToInstance(ScopeVersion, {
      ...state.currentScope,
      collaborators: state.currentScope.collaborators.filter((c) => c.user.id !== action.member.user.id),
      identity: {
        ...state.currentScope.identity,
        team: plainToInstance(ScopeTeam, {
          ...state.currentScope.identity.team,
          collaborators: state.currentScope.identity.team.collaborators.filter((c) => c.id !== action.member.id),
        }),
      },
    }),
    loading: { ...state.loading, teamMemberUpdate: true },
    loaded: { ...state.loaded, teamMemberUpdate: false },
  }
}

function removeScopeReviewer(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, teamMemberUpdate: true },
    loaded: { ...state.loaded, teamMemberUpdate: false },
  }
}

function removeScopeReviewerSuccessHandler(state: ScopeOverviewState, action) {
  return {
    ...state,
    currentScope: plainToInstance(ScopeVersion, {
      ...state.currentScope,
      reviewers: state.currentScope.reviewers.filter((c) => c.user.id !== action.member.user.id),
      identity: {
        ...state.currentScope.identity,
        team: plainToInstance(ScopeTeam, {
          ...state.currentScope.identity.team,
          reviewers: state.currentScope.identity.team.reviewers.filter((c) => c.id !== action.member.id),
        }),
      },
    }),
    loading: { ...state.loading, teamMemberUpdate: true },
    loaded: { ...state.loaded, teamMemberUpdate: false },
  }
}

function removeScopeApprover(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, teamMemberUpdate: true },
    loaded: { ...state.loaded, teamMemberUpdate: false },
  }
}

function removeScopeApproverSuccessHandler(state: ScopeOverviewState, action) {
  return {
    ...state,
    currentScope: plainToInstance(ScopeVersion, {
      ...state.currentScope,
      approvers: state.currentScope.approvers.filter((c) => c.voter.id !== action.member.user.id),
      identity: {
        ...state.currentScope.identity,
        team: plainToInstance(ScopeTeam, {
          ...state.currentScope.identity.team,
          approvers: state.currentScope.identity.team.approvers.filter((c) => c.id !== action.member.id),
        }),
      },
    }),
    loading: { ...state.loading, teamMemberUpdate: true },
    loaded: { ...state.loaded, teamMemberUpdate: false },
  }
}

function removeScopeTrafficker(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, teamMemberUpdate: true },
    loaded: { ...state.loaded, teamMemberUpdate: false },
  }
}

function removeScopeTraffickerSuccessHandler(state: ScopeOverviewState, action) {
  return {
    ...state,
    currentScope: plainToInstance(ScopeVersion, {
      ...state.currentScope,
      identity: {
        ...state.currentScope.identity,
        team: plainToInstance(ScopeTeam, {
          ...state.currentScope.identity.team,
          traffickers: state.currentScope.identity.team.traffickers.filter((c) => c.id !== action.member.id),
        }),
      },
    }),
    loading: { ...state.loading, teamMemberUpdate: true },
    loaded: { ...state.loaded, teamMemberUpdate: false },
  }
}

function scopeTeamFailHandler(state: ScopeOverviewState, action) {
  return {
    ...state,
    teamError: action.error,
    loading: { ...state.loading, teamMemberUpdate: false },
    loaded: { ...state.loaded, teamMemberUpdate: true },
  }
}

function deleteComponentFeeSuccess(state: ScopeOverviewState, action) {
  // Create an instance of the relevant class from the current state's currentScope
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)

  const deliverable = currentScopeInstance.deliverables.find((d) => d.id === action.deliverableId)
  if (deliverable && deliverable.components) {
    const component = deliverable.components.find((c) => c.id === action.componentId)
    if (component) {
      component.feeItems = component.feeItems?.filter((s) => s.id !== action.feeItemId)
    }
  }

  const allComponentFees = state.allComponentFees.filter((f) => f.id !== action.feeItemId)

  return { ...state, currentScope: currentScopeInstance, allComponentFees }
}

function deleteComponentFeeFail(state: ScopeOverviewState, error) {
  return { ...state, error }
}

function deleteDeliverableFeeSuccess(state: ScopeOverviewState, action) {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)

  const deliverable = currentScopeInstance.deliverables.find((d) => d.id === action.deliverableId)
  if (deliverable) {
    deliverable.feeItems = deliverable.feeItems?.filter((s) => s.id !== action.feeItemId)
  }

  const allDeliverableFees = state.allDeliverableFees.filter((f) => f.id !== action.feeItemId)

  return { ...state, currentScope: currentScopeInstance, allDeliverableFees }
}

function deleteDeliverableFeeFail(state: ScopeOverviewState, error: any) {
  return { ...state, error }
}

function deleteSectionFee(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, deleteSectionFee: true },
    loaded: { ...state.loaded, deleteSectionFee: false },
  }
}

function deleteSectionFeeSuccess(state: ScopeOverviewState, action) {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)

  const section = currentScopeInstance.sections.find((s) => s.id === action.sectionId)
  if (section) {
    section.feeItems = section.feeItems?.filter((s) => s.id !== action.feeItemId)
  }

  return {
    ...state,
    currentScope: currentScopeInstance,
    loading: { ...state.loading, deleteSectionFee: false },
    loaded: { ...state.loaded, deleteSectionFee: true },
  }
}

function deleteSectionFeeFail(state: ScopeOverviewState, error: any) {
  return {
    ...state,
    error,
    loading: { ...state.loading, deleteSectionFee: false },
    loaded: { ...state.loaded, deleteSectionFee: true },
  }
}

function deleteScopeFee(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, deleteScopeFee: true },
    loaded: { ...state.loaded, deleteScopeFee: false },
  }
}

function deleteScopeFeeSuccess(state: ScopeOverviewState, action) {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)

  currentScopeInstance.feeItems = currentScopeInstance.feeItems?.filter((s) => s.id !== action.feeItemId)

  return {
    ...state,
    currentScope: currentScopeInstance,
    loading: { ...state.loading, deleteScopeFee: true },
    loaded: { ...state.loaded, deleteScopeFee: false },
  }
}

function deleteScopeFeeFail(state: ScopeOverviewState, error: any) {
  return {
    ...state,
    error,
    loading: { ...state.loading, deleteScopeFee: true },
    loaded: { ...state.loaded, deleteScopeFee: false },
  }
}

function filterDeliverablesSuccessHandler(state: ScopeOverviewState, action) {
  return { ...state, filteredDeliverables: action.filteredDeliverables }
}

function createScopeFeeItemSuccessHandler(state: ScopeOverviewState, action) {
  return {
    ...state,
    currentScope: plainToInstance(ScopeVersion, {
      ...state.currentScope,
      feeItems: [...state.currentScope.feeItems, action.feeItem],
    }),
  }
}

function createSectionFeeItemSuccessHandler(state: ScopeOverviewState, action) {
  const sections = cloneDeep(state.currentScope.sections)
  sections.forEach((s) => {
    if (s.id == action.sectionId) {
      s.feeItems.push(action.feeItem)
    }
  })

  return { ...state, currentScope: plainToInstance(ScopeVersion, { ...state.currentScope, sections: sections }) }
}

function createDeliverableFeeItemSuccessHandler(state: ScopeOverviewState, action) {
  const deliverables = cloneDeep(state.currentScope.deliverables)
  deliverables.forEach((d) => {
    if (d.id == action.deliverableId) {
      d.feeItems.push(action.feeItem)
    }
  })

  return {
    ...state,
    currentScope: plainToInstance(ScopeVersion, { ...state.currentScope, deliverables: deliverables }),
  }
}

function editScopeFeeItemSuccessHandler(state: ScopeOverviewState, action) {
  const feeItems = cloneDeep(state.currentScope.feeItems)
  const index = feeItems.findIndex((f) => f.id == action.feeItemId)
  feeItems[index] = action.feeItem

  return { ...state, currentScope: plainToInstance(ScopeVersion, { ...state.currentScope, feeItems }) }
}

function editSectionFeeItemSuccessHandler(state: ScopeOverviewState, action) {
  const sections = cloneDeep(state.currentScope.sections)
  sections.forEach((s) => {
    if (s.id == action.sectionId) {
      const index = s.feeItems.findIndex((f) => f.id == action.feeItemId)
      s.feeItems[index] = action.feeItem
    }
  })

  return { ...state, currentScope: plainToInstance(ScopeVersion, { ...state.currentScope, sections }) }
}

function editDeliverableFeeItemSuccessHandler(state: ScopeOverviewState, action) {
  const deliverables = cloneDeep(state.currentScope.deliverables)
  deliverables.forEach((d) => {
    if (d.id == action.deliverableId) {
      const index = d.feeItems.findIndex((f) => f.id == action.feeItemId)
      d.feeItems[index] = action.feeItem
    }
  })

  const allDeliverableFees = state.allDeliverableFees.map((f) => (f.id === action.feeItemId ? action.feeItem : f))

  return {
    ...state,
    currentScope: plainToInstance(ScopeVersion, { ...state.currentScope, deliverables: deliverables }),
    allDeliverableFees,
  }
}

function editComponentFeeItemSuccessHandler(state: ScopeOverviewState, action) {
  const deliverables = cloneDeep(state.currentScope.deliverables)
  deliverables.forEach((d) => {
    if (d.id == action.deliverableId && d.components) {
      d.components.forEach((c) => {
        if (c.id == action.componentId) {
          const index = c.feeItems.findIndex((f) => f.id == action.feeItemId)
          c.feeItems[index] = action.feeItem
        }
      })
    }
  })

  const allComponentFees = state.allComponentFees.map((f) => (f.id === action.feeItemId ? action.feeItem : f))

  return {
    ...state,
    currentScope: plainToInstance(ScopeVersion, { ...state.currentScope, deliverables: deliverables }),
    allComponentFees: allComponentFees,
  }
}

function updateScopeDiscountSuccessHandler(state: ScopeOverviewState, action) {
  return { ...state, currentScope: plainToInstance(ScopeVersion, { ...state.currentScope, discount: action.discount }) }
}

function updateScopeMsaSuccessHandler(state: ScopeOverviewState, action) {
  return {
    ...state,
    currentScope: plainToInstance(ScopeVersion, { ...state.currentScope, msaLineItem: action.msaLineItem }),
  }
}

function updateScopeRetainedHoursStatsSuccessHandler(state: ScopeOverviewState, { scopeRetainedHoursStats }: any) {
  return {
    ...state,
    currentScope: plainToInstance(ScopeVersion, {
      ...state.currentScope,
      useRetainedHours: !state.currentScope.useRetainedHours,
    }),
    scopeRetainedHourStats: scopeRetainedHoursStats,
  }
}

function updateScopeSectionsOrderHandler(state: ScopeOverviewState, action) {
  return { ...state, currentScope: plainToInstance(ScopeVersion, { ...state.currentScope, sections: action.sections }) }
}

function updateDeliverablesOrderHandler(state: ScopeOverviewState, action) {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)
  if (action.deliverables.length) {
    if (currentScopeInstance.sections.length > 0) {
      currentScopeInstance.deliverables = currentScopeInstance.deliverables.map(
        (del) => action.deliverables.find((update) => update.id == del.id) || del
      )
    } else {
      currentScopeInstance.deliverables = action.deliverables
    }
  }

  return { ...state, currentScope: currentScopeInstance }
}

function deliverableTypeSearch(state: ScopeOverviewState) {
  return {
    ...state,
    availableDeliverableTypes: null,
    loading: { ...state.loading, deliverableTypeSearch: true },
    loaded: { ...state.loaded, deliverableTypeSearch: false },
  }
}

function deliverableTypeSearchSuccessHandler(state: ScopeOverviewState, { extendedDeliverables }: any) {
  return {
    ...state,
    availableDeliverableTypes: extendedDeliverables,
    loading: { ...state.loading, deliverableTypeSearch: false },
    loaded: { ...state.loaded, deliverableTypeSearch: true },
  }
}

function deliverableTypeSearchFailHandler(state: ScopeOverviewState, { error }: any) {
  return {
    ...state,
    error,
    loading: { ...state.loading, deliverableTypeSearch: false },
    loaded: { ...state.loaded, deliverableTypeSearch: true },
  }
}

function initiateTradeHandler(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, initiateTrade: true },
    loaded: { ...state.loaded, initiateTrade: false },
  }
}

function initiateTradeSuccessHandler(state: ScopeOverviewState, { tradedDeliverables }: any) {
  return {
    ...state,
    tradedDeliverables,
    loading: { ...state.loading, initiateTrade: false },
    loaded: { ...state.loaded, initiateTrade: true },
  }
}

function initiateTradeFailHandler(state: ScopeOverviewState, { error }: any) {
  return {
    ...state,
    error,
    loading: { ...state.loading, initiateTrade: false },
    loaded: { ...state.loaded, initiateTrade: true },
  }
}

function reopenTradeHandler(state: ScopeOverviewState) {
  return { ...state, loading: { ...state.loading, reopenTrade: true }, loaded: { ...state.loaded, reopenTrade: false } }
}

function reopenTradeSuccessHandler(state: ScopeOverviewState, { tradedDeliverables }: any) {
  return {
    ...state,
    tradedDeliverables,
    loading: { ...state.loading, reopenTrade: false },
    loaded: { ...state.loaded, reopenTrade: true },
  }
}

function reopenTradeFailHandler(state: ScopeOverviewState, { error }: any) {
  return {
    ...state,
    error,
    loading: { ...state.loading, reopenTrade: false },
    loaded: { ...state.loaded, reopenTrade: true },
  }
}

function stopTradeHandler(state: ScopeOverviewState) {
  return { ...state, loading: { ...state.loading, stopTrade: true }, loaded: { ...state.loaded, stopTrade: false } }
}

function stopTradeSuccessHandler(state: ScopeOverviewState, { tradedDeliverables }: any) {
  return {
    ...state,
    tradedDeliverables,
    loading: { ...state.loading, stopTrade: false },
    loaded: { ...state.loaded, stopTrade: true },
  }
}

function stopTradeFailHandler(state: ScopeOverviewState, { error }: any) {
  return {
    ...state,
    error,
    loading: { ...state.loading, stopTrade: false },
    loaded: { ...state.loaded, stopTrade: true },
  }
}

function completeTradeHandler(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, completeTrade: true },
    loaded: { ...state.loaded, completeTrade: false },
  }
}

function completeTradeSuccessHandler(state: ScopeOverviewState, { tradedDeliverables }: any) {
  return {
    ...state,
    tradedDeliverables,
    loading: { ...state.loading, completeTrade: false },
    loaded: { ...state.loaded, completeTrade: true },
  }
}

function completeTradeFailHandler(state: ScopeOverviewState, { error }: any) {
  return {
    ...state,
    error,
    loading: { ...state.loading, completeTrade: false },
    loaded: { ...state.loaded, completeTrade: true },
  }
}

function cancelTradeHandler(state: ScopeOverviewState) {
  return { ...state, loading: { ...state.loading, cancelTrade: true }, loaded: { ...state.loaded, cancelTrade: false } }
}

function cancelTradeSuccessHandler(state: ScopeOverviewState, { tradedDeliverables }: any) {
  return {
    ...state,
    tradedDeliverables,
    loading: { ...state.loading, cancelTrade: false },
    loaded: { ...state.loaded, cancelTrade: true },
  }
}

function cancelTradeFailHandler(state: ScopeOverviewState, { error }: any) {
  return {
    ...state,
    error,
    loading: { ...state.loading, cancelTrade: false },
    loaded: { ...state.loaded, cancelTrade: true },
  }
}

function confirmTradeHandler(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, confirmTrade: true },
    loaded: { ...state.loaded, confirmTrade: false },
  }
}

function confirmTradeSuccessHandler(state: ScopeOverviewState, { tradedDeliverables }: any) {
  return {
    ...state,
    tradedDeliverables,
    loading: { ...state.loading, confirmTrade: false },
    loaded: { ...state.loaded, confirmTrade: true },
  }
}

function confirmTradeFailHandler(state: ScopeOverviewState, { error }: any) {
  return {
    ...state,
    error,
    loading: { ...state.loading, confirmTrade: false },
    loaded: { ...state.loaded, confirmTrade: true },
  }
}

function applyLocationCardToDeliverableHandler(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, applyDeliverableLocationCard: true },
    loaded: { ...state.loaded, applyDeliverableLocationCard: false },
  }
}

function applyLocationCardToDeliverableSuccessHandler(state: ScopeOverviewState, { deliverable }: any) {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)

  const index = currentScopeInstance.deliverables.findIndex((d) => d.id === deliverable.id)
  currentScopeInstance.deliverables[index] = deliverable

  return {
    ...state,
    currentScope: currentScopeInstance,
    loading: { ...state.loading, applyDeliverableLocationCard: false },
    loaded: { ...state.loaded, applyDeliverableLocationCard: true },
  }
}

function applyLocationCardToDeliverableFailHandler(state: ScopeOverviewState, { error }: any) {
  return {
    ...state,
    error,
    loading: { ...state.loading, applyDeliverableLocationCard: false },
    loaded: { ...state.loaded, applyDeliverableLocationCard: true },
  }
}

function deleteScopeDiscountHandler(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, deleteScopeDiscount: true },
    loaded: { ...state.loaded, deleteScopeDiscount: false },
  }
}

function deleteScopeDiscountSuccessHandler(state: ScopeOverviewState) {
  return {
    ...state,
    currentScope: plainToInstance(ScopeVersion, {
      ...state.currentScope,
      discount: null,
    }),
    loading: { ...state.loading, deleteScopeDiscount: false },
    loaded: { ...state.loaded, deleteScopeDiscount: true },
  }
}

function deleteScopeDiscountFailHandler(state: ScopeOverviewState, { error }: any) {
  return {
    ...state,
    error,
    loading: { ...state.loading, deleteScopeDiscount: false },
    loaded: { ...state.loaded, deleteScopeDiscount: true },
  }
}

function deleteScopeMsaHandler(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, deleteScopeMsa: true },
    loaded: { ...state.loaded, deleteScopeMsa: false },
  }
}

function deleteScopeMsaSuccessHandler(state: ScopeOverviewState) {
  return {
    ...state,
    currentScope: plainToInstance(ScopeVersion, { ...state.currentScope, msaLineItem: null }),
    loading: { ...state.loading, deleteScopeMsa: false },
    loaded: { ...state.loaded, deleteScopeMsa: true },
  }
}

function deleteScopeMsaFailHandler(state: ScopeOverviewState, { error }: any) {
  return {
    ...state,
    error,
    loading: { ...state.loading, deleteScopeMsa: false },
    loaded: { ...state.loaded, deleteScopeMsa: true },
  }
}

function updateCurrentScope(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, updateCurrentScope: true },
    loaded: { ...state.loaded, updateCurrentScope: false },
  }
}

function updateCurrentScopeSuccessHandler(state: ScopeOverviewState, { currentScope }: any) {
  const currentScopeInstance = plainToInstance(ScopeVersion, currentScope)
  remapDeliverablesInTradedHierarchy(currentScopeInstance)

  return {
    ...state,
    currentScope: currentScopeInstance,
    loading: { ...state.loading, updateCurrentScope: false },
    loaded: { ...state.loaded, updateCurrentScope: true },
  }
}

function updateCurrentScopeFailHandler(state: ScopeOverviewState, { error }: any) {
  return {
    ...state,
    error,
    loading: { ...state.loading, updateCurrentScope: false },
    loaded: { ...state.loaded, updateCurrentScope: true },
  }
}

function addDeliverableCommentSuccessHandler(state: ScopeOverviewState, action): ScopeOverviewState {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)

  const deliverable = currentScopeInstance.deliverables.find((del) => del.id == action.deliverableId)
  if (deliverable) deliverable.comments = [...(deliverable.comments || []), action.comment]

  return {
    ...state,
    currentScope: currentScopeInstance,
    loading: { ...state.loading, addScopeDeliverable: false },
    loaded: { ...state.loaded, addScopeDeliverable: true },
  }
}

function addComponentCommentSuccessHandler(state: ScopeOverviewState, action): ScopeOverviewState {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)

  const deliverable = currentScopeInstance.deliverables.find((del) => del.id == action.deliverableId)
  if (deliverable) {
    const component = deliverable.components.find((com) => com.id == action.componentId)
    if (component) component.comments = [...(component.comments || []), action.comment]
  }

  return {
    ...state,
    currentScope: currentScopeInstance,
    loading: { ...state.loading, addScopeDeliverable: false },
    loaded: { ...state.loaded, addScopeDeliverable: true },
  }
}

function addDepartmentCommentSuccessHandler(state: ScopeOverviewState, action): ScopeOverviewState {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)

  const deliverable = currentScopeInstance.deliverables.find((del) => del.id == action.deliverableId)
  if (deliverable) {
    const component = deliverable.components.find((com) => com.id == action.componentId)
    if (component) {
      const department = component.departments.find((dep) => dep.id == action.departmentId)
      if (department) department.comments = [...(department.comments || []), action.comment]
    }
  }

  return {
    ...state,
    currentScope: currentScopeInstance,
    loading: { ...state.loading, addScopeDeliverable: false },
    loaded: { ...state.loaded, addScopeDeliverable: true },
  }
}

function addRoleCommentSuccessHandler(state: ScopeOverviewState, action): ScopeOverviewState {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)

  const deliverable = currentScopeInstance.deliverables.find((del) => del.id == action.deliverableId)
  if (deliverable) {
    const component = deliverable.components.find((com) => com.id == action.componentId)
    if (component) {
      component.departments.forEach((department) => {
        const role = department.roles.find((role) => role.id == action.roleId)
        if (role) role.comments = [...(role.comments || []), action.comment]
      })
    }
  }

  return {
    ...state,
    currentScope: currentScopeInstance,
    loading: { ...state.loading, addScopeDeliverable: false },
    loaded: { ...state.loaded, addScopeDeliverable: true },
  }
}

function addDeliverableSectionCommentSuccessHandler(state: ScopeOverviewState, action): ScopeOverviewState {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)

  const deliverable = currentScopeInstance.deliverables.find((del) => del.id == action.deliverableId)
  if (deliverable) {
    const section = deliverable.sections.find((section) => section.id == action.sectionId)
    if (section) section.comments = [...(section.comments || []), action.comment]
  }

  return {
    ...state,
    currentScope: currentScopeInstance,
    loading: { ...state.loading, addScopeDeliverable: false },
    loaded: { ...state.loaded, addScopeDeliverable: true },
  }
}

function addScopeSectionCommentSuccessHandler(state: ScopeOverviewState, action): ScopeOverviewState {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)

  const section = currentScopeInstance.sections.find((section) => section.id == action.sectionId)
  if (section) section.comments = [...(section.comments || []), action.comment]

  return {
    ...state,
    currentScope: currentScopeInstance,
    loading: { ...state.loading, addScopeDeliverable: false },
    loaded: { ...state.loaded, addScopeDeliverable: true },
  }
}

function addDeliverableTPCCommentSuccessHandler(state: ScopeOverviewState, action): ScopeOverviewState {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)

  const deliverable = currentScopeInstance.deliverables.find((del) => del.id == action.deliverableId)
  if (deliverable) {
    const tpc = deliverable.thirdPartyCosts.find((tpc) => tpc.id == action.tpcId)
    if (tpc) tpc.comments = [...(tpc.comments || []), action.comment]
  }

  return {
    ...state,
    currentScope: currentScopeInstance,
    loading: { ...state.loading, addScopeDeliverable: false },
    loaded: { ...state.loaded, addScopeDeliverable: true },
  }
}

function addScopeTPCCommentSuccessHandler(state: ScopeOverviewState, action): ScopeOverviewState {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)

  const tpc = currentScopeInstance.thirdPartyCosts.find((tpc) => tpc.id == action.tpcId)
  if (tpc) tpc.comments = [...(tpc.comments || []), action.comment]

  return {
    ...state,
    currentScope: currentScopeInstance,
    loading: { ...state.loading, addScopeDeliverable: false },
    loaded: { ...state.loaded, addScopeDeliverable: true },
  }
}

function addComponentTPCCommentSuccessHandler(state: ScopeOverviewState, action): ScopeOverviewState {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)

  const deliverable = currentScopeInstance.deliverables.find((del) => del.id == action.deliverableId)
  if (deliverable) {
    const component = deliverable.components.find((com) => com.id == action.componentId)
    if (component) {
      const tpc = component.thirdPartyCosts.find((tpc) => tpc.id == action.tpcId)
      if (tpc) tpc.comments = [...(tpc.comments || []), action.comment]
    }
  }

  return {
    ...state,
    currentScope: currentScopeInstance,
    loading: { ...state.loading, addScopeDeliverable: false },
    loaded: { ...state.loaded, addScopeDeliverable: true },
  }
}

function addScopeCommentSuccessHandler(state: ScopeOverviewState, action): ScopeOverviewState {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)

  currentScopeInstance.comments = [...(currentScopeInstance.comments || []), action.comment]

  return {
    ...state,
    currentScope: currentScopeInstance,
    loading: { ...state.loading, addScopeDeliverable: false },
    loaded: { ...state.loaded, addScopeDeliverable: true },
  }
}

function getDeliverableAndComponentFeesHandler(state: ScopeOverviewState) {
  return { ...state, loading: { ...state.loading, allFees: true }, loaded: { ...state.loaded, allFees: false } }
}

function getDeliverableAndComponentFeesSuccessHandler(state: ScopeOverviewState, action) {
  let currentScopeInstance: ScopeVersion
  if (action.deliverableFeeItems?.length) {
    currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)

    let deliverable
    action.deliverableFeeItems.forEach((feeItem: FeeItemInstance) => {
      if (deliverable?.id !== feeItem.deliverable.id)
        deliverable = currentScopeInstance.deliverables.find((del) => del.id == feeItem.deliverable.id)

      if (deliverable && !deliverable.componentsInitialised) deliverable.feeItems.push(feeItem)
    })
  } else {
    currentScopeInstance = state.currentScope
  }

  return {
    ...state,
    allComponentFees: action.componentFeeItems,
    allDeliverableFees: action.deliverableFeeItems,
    loading: { ...state.loading, allFees: false },
    loaded: { ...state.loaded, allFees: true },
  }
}

function getDeliverableAndComponentFeesFailHandler(state: ScopeOverviewState) {
  return { ...state, loading: { ...state.loading, allFees: false }, loaded: { ...state.loaded, allFees: false } }
}

function getAllDeliverableTPCsHandler(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, allTPCs: true },
    loaded: { ...state.loaded, allThirdPartyCosts: false },
  }
}

function getAllDeliverableTPCsSuccessHandler(state: ScopeOverviewState, action) {
  let currentScopeInstance: ScopeVersion
  if (action.deliverableThirdPartyCosts?.length) {
    currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)

    let deliverable
    action.deliverableThirdPartyCosts.forEach((thirdPartyCost: ThirdPartyCost) => {
      if (deliverable?.id !== thirdPartyCost.deliverable.id)
        deliverable = currentScopeInstance.deliverables.find((del) => del.id == thirdPartyCost.deliverable.id)

      if (deliverable && !deliverable.componentsInitialised) deliverable.thirdPartyCosts.push(thirdPartyCost)
    })
  } else {
    currentScopeInstance = state.currentScope
  }

  return {
    ...state,
    currentScope: currentScopeInstance,
    allDeliverableTPCs: action.deliverableThirdPartyCosts,
    allComponentTPCs: action.componentThirdPartyCosts,
    allSectionTPCs: action.sectionThirdPartyCosts,
    loading: { ...state.loading, allTPCs: false },
    loaded: { ...state.loaded, allThirdPartyCosts: true },
  }
}

function getAllDeliverableTPCsFailHandler(state: ScopeOverviewState) {
  return {
    ...state,
    loading: { ...state.loading, allTPCs: false },
    loaded: { ...state.loaded, allThirdPartyCosts: false },
  }
}

function searchComponentsForScopeSuccessHandler(state: ScopeOverviewState, action: any) {
  return {
    ...state,
    scopeComponents: action.components.filter(
      (item, i, arr) => arr.findIndex((t) => t.name === item.name && t.originalName === item.originalName) === i
    ),
  }
}

function searchNonDistinctComponentsSuccessHandler(state: ScopeOverviewState, action: any) {
  return { ...state, nonDistinctComponents: action.components }
}

function searchDepartmentsForScopeSuccessHandler(state: ScopeOverviewState, action: any) {
  return { ...state, scopeDepartments: action.departments }
}

function searchRolesForScopeSuccessHandler(state: ScopeOverviewState, action: any) {
  return { ...state, scopeRoles: action.roles }
}

function applyLocationToRoleSuccessHandler(state: ScopeOverviewState, action: any) {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)

  currentScopeInstance.deliverables.forEach((deliverable) => {
    const department = deliverable.components[0].departments.find((d) => d.source.id === action.sourceDepartmentId)
    if (department) {
      const role = department.roles.find((r) => r.source.id == action.sourceRoleId)
      if (role) {
        role.ratecardLocationRole = action.ratecardLocationRole
        role.locationName = action.locationName
        role.rateCardRate = action.rateCardRate
        role.rateCardCost = action.rateCardCost
      }
    }
  })

  return { ...state, currentScope: currentScopeInstance }
}

function setDialogIdHandler(state: ScopeOverviewState, action: any) {
  return { ...state, dialogId: action.id }
}

function getScenarioSuccessHandler(state: ScopeOverviewState, action: any) {
  const configuration = state.currentScope?.configuration || {}
  const scenario = mapScenario(plainToInstance(Scenario, action.scenario), configuration)

  return { ...state, scenario: scenario }
}

function updateScenarioHandler(state: ScopeOverviewState, action: any) {
  return {
    ...state,
    scenario: action.scenario,
    configurationHasUpdates: state.configurationHasUpdates || action.hasUpdates,
  }
}

function resetScenarioHandler(state: ScopeOverviewState) {
  return { ...state, scenario: undefined, configurationHasUpdates: false }
}

const saveConfigurationSuccessHandler = (state: ScopeOverviewState) => ({
  ...state,
  configurationHasUpdates: false,
})

const updateConfigurationHandler = (state: ScopeOverviewState, { configuration }) => ({
  ...state,
  currentScope: plainToInstance(ScopeVersion, { ...state.currentScope, configuration, configurationSubmitted: false }),
})

const moveDeliverableHandler = (state: ScopeOverviewState, { fromSectionId, deliverable }: MoveDeliverablePayload) => ({
  ...state,
  loading: {
    ...state.loading,
    section: {
      ...state.loading.section,
      [fromSectionId]: true,
      [deliverable.id]: true,
    },
  },
  loaded: {
    ...state.loaded,
    section: {
      ...state.loaded.section,
      [fromSectionId]: false,
      [deliverable.id]: false,
    },
  },
})
const moveDeliverableSuccessHandler = (
  state: ScopeOverviewState,
  { fromSectionId, deliverable }: MoveDeliverablePayload
) => ({
  ...state,
  displayedDeliverablesCount:
    fromSectionId && !deliverable.section?.id ? state.displayedDeliverablesCount + 1 : state.displayedDeliverablesCount,
  loading: {
    ...state.loading,
    section: {
      ...state.loading.section,
      [fromSectionId]: false,
      [deliverable.id]: false,
    },
  },
  loaded: {
    ...state.loaded,
    section: {
      ...state.loaded.section,
      [fromSectionId]: true,
      [deliverable.id]: true,
    },
  },
})
const moveDeliverableFailHandler = (
  state: ScopeOverviewState,
  { fromSectionId, deliverable }: MoveDeliverablePayload
) => ({
  ...state,
  loading: {
    ...state.loading,
    section: {
      ...state.loading.section,
      [fromSectionId]: false,
      [deliverable.id]: false,
    },
  },
  loaded: {
    ...state.loaded,
    section: {
      ...state.loaded.section,
      [fromSectionId]: true,
      [deliverable.id]: true,
    },
  },
})

function getScopeSectionLoadingHandler(loading: boolean) {
  return (state: ScopeOverviewState, { sectionId }: { sectionId: number }) => ({
    ...state,
    loading: { ...state.loading, section: { ...state.loading.section, [sectionId]: loading } },
  })
}

function getScopeSectionSuccessHandler(state: ScopeOverviewState, { section }) {
  const currentScopeInstance = plainToInstance(ScopeVersion, state.currentScope)
  if (!currentScopeInstance) return state

  const mappedSection = DeliverableMoneyUtils.prepareSection(section)
  currentScopeInstance.sections = currentScopeInstance.sections.map((s) => (s.id === section.id ? mappedSection : s))

  return {
    ...state,
    currentScope: currentScopeInstance,
    loading: { ...state.loading, section: { ...state.loading.section, [section.id]: false } },
    loaded: { ...state.loaded, section: { ...state.loaded.section, [section.id]: true } },
  }
}

const ScopeOverviewReducer = createReducer(
  scopeOverviewInitialState,
  on(ScopeOverviewActions.getActivitiesSuccess, getActivityCommentsSuccessHandler),
  on(ScopeOverviewActions.submitCommentSuccess, submitCommentSuccessHandler),
  on(ScopeOverviewActions.getScopeRetainedStats, getScopeRetainedStats),
  on(ScopeOverviewActions.getScopeRetainedStatsSuccess, getScopeRetainedStatsSuccess),
  on(ScopeOverviewActions.getScopeRetainedStatsFail, getScopeRetainedStatsFail),
  on(ScopeOverviewActions.getSowRetainedStats, getSowRetainedStats),
  on(ScopeOverviewActions.getSowRetainedStatsSuccess, getSowRetainedStatsSuccess),
  on(ScopeOverviewActions.getSowRetainedStatsFail, getSowRetainedStatsFail),
  on(ScopeOverviewActions.getThirdPartyBalance, getThirdPartyCostBalance),
  on(ScopeOverviewActions.getThirdPartyBalanceSuccess, getThirdPartyCostBalanceSuccess),
  on(ScopeOverviewActions.getThirdPartyBalanceFail, getThirdPartyCostBalanceFail),
  on(ScopeOverviewActions.changeScopeVersion, changeScopeVersionHandler),
  on(ScopeOverviewActions.changeScopeVersionSuccess, changeScopeVersionSuccess),
  on(ScopeOverviewActions.changeScopeVersionFail, changeScopeVersionFail),
  on(ScopeOverviewActions.addScopeSection, addScopeSection),
  on(ScopeOverviewActions.addScopeSectionSuccess, addScopeSectionSuccess),
  on(ScopeOverviewActions.addScopeSectionFail, addScopeSectionFail),
  on(ScopeOverviewActions.archiveScope, archiveScope),
  on(ScopeOverviewActions.archiveScopeSuccess, archiveScopeSuccess),
  on(ScopeOverviewActions.archiveScopeFail, archiveScopeFail),
  on(ScopeOverviewActions.unarchiveScope, unarchiveScope),
  on(ScopeOverviewActions.unarchiveScopeSuccess, unarchiveScopeSuccess),
  on(ScopeOverviewActions.unarchiveScopeFail, unarchiveScopeFail),
  on(ScopeOverviewActions.getAllTasks, getAllTasksHandler),
  on(ScopeOverviewActions.getAllTasksSuccess, getAllTasksSuccessHandler),
  on(ScopeOverviewActions.getAllTasksFail, getAllTasksFailHandler),
  on(ScopeOverviewActions.getAvailableScopeDeliverableTypes, getAvailableDeliverableTypes),
  on(ScopeOverviewActions.getAvailableScopeDeliverableTypesSuccess, getAvailableDeliverableTypesSuccess),
  on(ScopeOverviewActions.getAvailableScopeDeliverableTypesFail, getAvailableDeliverableTypesFail),
  on(ScopeOverviewActions.addScopeDeliverable, addScopeDeliverable),
  on(ScopeOverviewActions.addScopeDeliverableSuccess, addScopeDeliverableSuccessHandler),
  on(ScopeOverviewActions.addScopeDeliverableFail, addScopeDeliverableFailHandler),
  on(ScopeOverviewActions.addScopeDeliverablesSuccess, addScopeDeliverablesSuccessHandler),
  on(ScopeOverviewActions.duplicateScopeDeliverable, duplicateScopeDeliverable),
  on(ScopeOverviewActions.duplicateScopeDeliverableSuccess, duplicateScopeDeliverableSuccessHandler),
  on(ScopeOverviewActions.duplicateScopeDeliverableFail, duplicateScopeDeliverableFailHandler),
  on(ScopeOverviewActions.moveComponentToDeliverable, moveComponentToDeliverable),
  on(ScopeOverviewActions.moveComponentToDeliverableSuccess, moveComponentToDeliverableSuccessHandler),
  on(ScopeOverviewActions.moveComponentToDeliverableFail, moveComponentToDeliverableFailHandler),
  on(ScopeOverviewActions.updateScopeDeliverable, updateScopeDeliverable),
  on(ScopeOverviewActions.updateScopeDeliverableSuccess, updateScopeDeliverableSuccessHandler),
  on(ScopeOverviewActions.updateScopeDeliverableFail, updateScopeDeliverableFailHandler),
  on(ScopeOverviewActions.clearScopeTaskRoles, clearScopeTaskRoles),
  on(ScopeOverviewActions.clearScopeTaskRolesSuccess, clearScopeTaskRolesSuccessHandler),
  on(ScopeOverviewActions.clearScopeTaskRolesFail, clearScopeTaskRolesFailHandler),
  on(ScopeOverviewActions.updateScopeSection, updateScopeSection),
  on(ScopeOverviewActions.updateScopeSectionSuccess, updateScopeSectionSuccessHandler),
  on(ScopeOverviewActions.updateScopeSectionFail, updateScopeSectionFailHandler),
  on(ScopeOverviewActions.updateScopeComponent, updateScopeComponent),
  on(ScopeOverviewActions.updateScopeComponentSuccess, updateScopeComponentSuccessHandler),
  on(ScopeOverviewActions.updateScopeComponentFail, updateScopeComponentFailHandler),
  on(ScopeOverviewActions.updateDeliverableSection, updateDeliverableSectionHandler),
  on(ScopeOverviewActions.updateDeliverableSectionSuccess, updateDeliverableSectionSuccessHandler),
  on(ScopeOverviewActions.updateDeliverableSectionFail, updateDeliverableSectionFailHandler),
  on(ScopeOverviewActions.overrideScopeComponentSuccess, overrideScopeComponentSuccessHandler),
  on(ScopeOverviewActions.updateRole, updateRoleHandler),
  on(ScopeOverviewActions.overrideRole, overrideRoleHandler),
  on(ScopeOverviewActions.overrideRoleSuccess, overrideRoleSuccessHandler),
  on(ScopeOverviewActions.overrideRoleFail, overrideRoleFailHandler),
  on(ScopeOverviewActions.updateDeliverableTpc, updateTpcHandler),
  on(ScopeOverviewActions.updateDeliverableTpcSuccess, updateDeliverableTpcSuccessHandler),
  on(ScopeOverviewActions.updateDeliverableTpcFail, updateTpcFailHandler),
  on(ScopeOverviewActions.updateComponentTpc, updateTpcHandler),
  on(ScopeOverviewActions.updateComponentTpcSuccess, updateComponentTpcSuccessHandler),
  on(ScopeOverviewActions.updateComponentTpcFail, updateTpcFailHandler),
  on(ScopeOverviewActions.updateDeliverableSectionTpc, updateTpcHandler),
  on(ScopeOverviewActions.updateDeliverableSectionTpcSuccess, updateDeliverableSectionTpcSuccessHandler),
  on(ScopeOverviewActions.updateDeliverableSectionTpcFail, updateTpcFailHandler),
  on(ScopeOverviewActions.createScopeTpcSuccess, createScopeTpcSuccessHandler),
  on(ScopeOverviewActions.updateScopeTpcSuccess, updateScopeTpcSuccessHandler),
  on(ScopeOverviewActions.deleteScopeTpcSuccess, deleteScopeTpcSuccessHandler),
  on(ScopeOverviewActions.createLibraryDeliverableTemplate, createLibraryTemplate),
  on(ScopeOverviewActions.createLibraryDeliverableTemplateSuccess, createLibraryTemplateSuccess),
  on(ScopeOverviewActions.createLibraryDeliverableTemplateFail, createLibraryTemplateFail),
  on(ScopeOverviewActions.createLibraryDeliverableEntry, createLibraryEntry),
  on(ScopeOverviewActions.createLibraryDeliverableEntrySuccess, createLibraryEntrySuccess),
  on(ScopeOverviewActions.createLibraryDeliverableEntry, createLibraryEntryFail),
  on(ScopeOverviewActions.getOutputTemplate, getOutputTemplate),
  on(ScopeOverviewActions.getOutputTemplateSuccess, getOutputTemplateSuccess),
  on(ScopeOverviewActions.getOutputTemplateFail, getOutputTemplateFail),
  on(ScopeOverviewActions.updateTemplateFail, updateTemplateFail),
  on(ScopeOverviewActions.previewScopeDocx, previewScopeDocx),
  on(ScopeOverviewActions.previewScopeDocxSuccess, previewScopeDocxSuccess),
  on(ScopeOverviewActions.previewScopeDocxFail, previewScopeDocxFail),
  on(ScopeOverviewActions.getSecondParty, getSecondParty),
  on(ScopeOverviewActions.getSecondPartySuccess, getSecondPartySuccess),
  on(ScopeOverviewActions.getSecondPartyFail, getSecondPartyFail),
  on(ScopeOverviewActions.getOfficeLocation, getOfficeLocation),
  on(ScopeOverviewActions.getOfficeLocationSuccess, getOfficeLocationSuccess),
  on(ScopeOverviewActions.getOfficeLocationFail, getOfficeLocationFail),
  on(ScopeOverviewActions.getSowRetainedTeamStats, getSowRetainedTeamStats),
  on(ScopeOverviewActions.getSowRetainedTeamStatsSuccess, getSowRetainedTeamStatsSuccess),
  on(ScopeOverviewActions.getSowRetainedTeamStatsFail, getSowRetainedTeamStatsFail),
  on(ScopeOverviewActions.getScopeCustomFieldsDefinitionOutdated, getScopeCustomFieldsDefinitionOutdated),
  on(ScopeOverviewActions.getScopeCustomFieldsDefinitionOutdatedSuccess, getScopeCustomFieldsDefinitionOutdatedSuccess),
  on(ScopeOverviewActions.getScopeCustomFieldsDefinitionOutdatedFail, getScopeCustomFieldsDefinitionOutdatedFail),
  on(ScopeOverviewActions.getAllRateCardsForScopeCreate, getAllRateCardsForScopeCreate),
  on(ScopeOverviewActions.getAllRateCardsForScopeCreateSuccess, getAllRateCardsForScopeCreateSuccess),
  on(ScopeOverviewActions.getAllRateCardsForScopeCreateFail, getAllRateCardsForScopeCreateFail),
  on(ScopeOverviewActions.getDefaultRateCard, getDefaultRateCard),
  on(ScopeOverviewActions.getDefaultRateCardSuccess, getDefaultRateCardSuccess),
  on(ScopeOverviewActions.getDefaultRateCardFail, getDefaultRateCardFail),
  on(ScopeOverviewActions.deleteScopeSection, deleteScopeSection),
  on(ScopeOverviewActions.deleteScopeSectionSuccess, deleteScopeSectionSuccess),
  on(ScopeOverviewActions.deleteScopeSectionFail, deleteScopeSectionFail),
  on(ScopeOverviewActions.updateScope, updateScope),
  on(ScopeOverviewActions.updateScopeSuccess, updateScopeSuccessHandler),
  on(ScopeOverviewActions.updateScopeFail, updateScopeFailHandler),
  on(ScopeOverviewActions.refreshOutdatedCustomFields, refreshOutdatedCustomFields),
  on(ScopeOverviewActions.refreshOutdatedCustomFieldsSuccess, refreshOutdatedCustomFieldsSuccessHandler),
  on(ScopeOverviewActions.refreshOutdatedCustomFieldsFail, refreshOutdatedCustomFieldsFailHandler),
  on(ScopeOverviewActions.addScopeCollaborator, addScopeCollaborator),
  on(ScopeOverviewActions.addScopeCollaboratorSuccess, addScopeCollaboratorSuccessHandler),
  on(ScopeOverviewActions.addScopeReviewer, addScopeReviewer),
  on(ScopeOverviewActions.addScopeReviewerSuccess, addScopeReviewerSuccessHandler),
  on(ScopeOverviewActions.addScopeApprover, addScopeApprover),
  on(ScopeOverviewActions.addScopeApproverSuccess, addScopeApproverSuccessHandler),
  on(ScopeOverviewActions.addScopeTrafficker, addScopeTrafficker),
  on(ScopeOverviewActions.addScopeTraffickerSuccess, addScopeTraffickerSuccessHandler),
  on(ScopeOverviewActions.updateTeamMember, updateTeamMember),
  on(ScopeOverviewActions.updateTeamMemberSuccess, updateTeamMemberSuccessHandler),
  on(ScopeOverviewActions.removeScopeCollaborator, removeScopeCollaborator),
  on(ScopeOverviewActions.removeScopeCollaboratorSuccess, removeScopeCollaboratorSuccessHandler),
  on(ScopeOverviewActions.removeScopeReviewer, removeScopeReviewer),
  on(ScopeOverviewActions.removeScopeReviewerSuccess, removeScopeReviewerSuccessHandler),
  on(ScopeOverviewActions.removeScopeApprover, removeScopeApprover),
  on(ScopeOverviewActions.removeScopeApproverSuccess, removeScopeApproverSuccessHandler),
  on(ScopeOverviewActions.removeScopeTrafficker, removeScopeTrafficker),
  on(ScopeOverviewActions.removeScopeTraffickerSuccess, removeScopeTraffickerSuccessHandler),
  on(ScopeOverviewActions.scopeTeamFail, scopeTeamFailHandler),
  on(ScopeOverviewActions.deleteDeliverable, deleteDeliverable),
  on(ScopeOverviewActions.deleteDeliverableSuccess, deleteDeliverableSuccess),
  on(ScopeOverviewActions.deleteDeliverableFail, deleteDeliverableFail),
  on(ScopeOverviewActions.deleteComponent, deleteComponent),
  on(ScopeOverviewActions.deleteComponentSuccess, deleteComponentSuccess),
  on(ScopeOverviewActions.deleteComponentFail, deleteComponentFail),
  on(ScopeOverviewActions.deleteDeliverableFeeItemSuccess, deleteDeliverableFeeSuccess),
  on(ScopeOverviewActions.deleteDeliverableFeeItemFail, deleteDeliverableFeeFail),
  on(ScopeOverviewActions.deleteComponentFeeSuccess, deleteComponentFeeSuccess),
  on(ScopeOverviewActions.deleteComponentFeeFail, deleteComponentFeeFail),
  on(ScopeOverviewActions.deleteScopeFeeItem, deleteScopeFee),
  on(ScopeOverviewActions.deleteScopeFeeItemSuccess, deleteScopeFeeSuccess),
  on(ScopeOverviewActions.deleteScopeFeeItemFail, deleteScopeFeeFail),
  on(ScopeOverviewActions.deleteSectionFeeItem, deleteSectionFee),
  on(ScopeOverviewActions.deleteSectionFeeItemSuccess, deleteSectionFeeSuccess),
  on(ScopeOverviewActions.updateScopeDiscountSuccess, updateScopeDiscountSuccessHandler),
  on(ScopeOverviewActions.updateScopeMsaSuccess, updateScopeMsaSuccessHandler),
  on(ScopeOverviewActions.updateScopeRetainedHoursStatsSuccess, updateScopeRetainedHoursStatsSuccessHandler),
  on(ScopeOverviewActions.deleteSectionFeeItemFail, deleteSectionFeeFail),
  on(ScopeOverviewActions.setScopeNotificationEvent, setScopeNotificationEvent),
  on(ScopeOverviewActions.filterDeliverablesSuccess, filterDeliverablesSuccessHandler),
  on(ScopeOverviewActions.createScopeFeeItemSuccess, createScopeFeeItemSuccessHandler),
  on(ScopeOverviewActions.createSectionFeeItemSuccess, createSectionFeeItemSuccessHandler),
  on(ScopeOverviewActions.createDeliverableFeeItemSuccess, createDeliverableFeeItemSuccessHandler),
  on(ScopeOverviewActions.editScopeFeeItemSuccess, editScopeFeeItemSuccessHandler),
  on(ScopeOverviewActions.editSectionFeeItemSuccess, editSectionFeeItemSuccessHandler),
  on(ScopeOverviewActions.editDeliverableFeeItemSuccess, editDeliverableFeeItemSuccessHandler),
  on(ScopeOverviewActions.editComponentFeeItemSuccess, editComponentFeeItemSuccessHandler),
  on(ScopeOverviewActions.updateScopeSectionsOrder, updateScopeSectionsOrderHandler),
  on(ScopeOverviewActions.updateDeliverablesOrder, updateDeliverablesOrderHandler),
  on(ScopeOverviewActions.deliverableTypeSearch, deliverableTypeSearch),
  on(ScopeOverviewActions.deliverableTypeSearchSuccess, deliverableTypeSearchSuccessHandler),
  on(ScopeOverviewActions.deliverableTypeSearchFail, deliverableTypeSearchFailHandler),
  on(ScopeOverviewActions.initiateTrade, initiateTradeHandler),
  on(ScopeOverviewActions.initiateTradeSuccess, initiateTradeSuccessHandler),
  on(ScopeOverviewActions.initiateTradeFail, initiateTradeFailHandler),
  on(ScopeOverviewActions.updateCurrentScope, updateCurrentScope),
  on(ScopeOverviewActions.updateCurrentScopeSuccess, updateCurrentScopeSuccessHandler),
  on(ScopeOverviewActions.updateCurrentScopeFail, updateCurrentScopeFailHandler),
  on(ScopeOverviewActions.reopenTrade, reopenTradeHandler),
  on(ScopeOverviewActions.reopenTradeSuccess, reopenTradeSuccessHandler),
  on(ScopeOverviewActions.reopenTradeFail, reopenTradeFailHandler),
  on(ScopeOverviewActions.stopTrade, stopTradeHandler),
  on(ScopeOverviewActions.stopTradeSuccess, stopTradeSuccessHandler),
  on(ScopeOverviewActions.stopTradeFail, stopTradeFailHandler),
  on(ScopeOverviewActions.completeTrade, completeTradeHandler),
  on(ScopeOverviewActions.completeTradeSuccess, completeTradeSuccessHandler),
  on(ScopeOverviewActions.completeTradeFail, completeTradeFailHandler),
  on(ScopeOverviewActions.cancelTrade, cancelTradeHandler),
  on(ScopeOverviewActions.cancelTradeSuccess, cancelTradeSuccessHandler),
  on(ScopeOverviewActions.cancelTradeFail, cancelTradeFailHandler),
  on(ScopeOverviewActions.confirmTrade, confirmTradeHandler),
  on(ScopeOverviewActions.confirmTradeSuccess, confirmTradeSuccessHandler),
  on(ScopeOverviewActions.confirmTradeFail, confirmTradeFailHandler),
  on(ScopeOverviewActions.applyLocationCardToDeliverable, applyLocationCardToDeliverableHandler),
  on(ScopeOverviewActions.applyLocationCardToDeliverableSuccess, applyLocationCardToDeliverableSuccessHandler),
  on(ScopeOverviewActions.applyLocationCardToDeliverableFail, applyLocationCardToDeliverableFailHandler),
  on(ScopeOverviewActions.deleteScopeDiscount, deleteScopeDiscountHandler),
  on(ScopeOverviewActions.deleteScopeDiscountSuccess, deleteScopeDiscountSuccessHandler),
  on(ScopeOverviewActions.deleteScopeDiscountFail, deleteScopeDiscountFailHandler),
  on(ScopeOverviewActions.deleteScopeMsa, deleteScopeMsaHandler),
  on(ScopeOverviewActions.deleteScopeMsaSuccess, deleteScopeMsaSuccessHandler),
  on(ScopeOverviewActions.deleteScopeMsaFail, deleteScopeMsaFailHandler),
  on(ScopeOverviewActions.addDeliverableCommentSuccess, addDeliverableCommentSuccessHandler),
  on(ScopeOverviewActions.addComponentCommentSuccess, addComponentCommentSuccessHandler),
  on(ScopeOverviewActions.addDepartmentCommentSuccess, addDepartmentCommentSuccessHandler),
  on(ScopeOverviewActions.addRoleCommentSuccess, addRoleCommentSuccessHandler),
  on(ScopeOverviewActions.addDeliverableSectionCommentSuccess, addDeliverableSectionCommentSuccessHandler),
  on(ScopeOverviewActions.addScopeSectionCommentSuccess, addScopeSectionCommentSuccessHandler),
  on(ScopeOverviewActions.addDeliverableTPCCommentSuccess, addDeliverableTPCCommentSuccessHandler),
  on(ScopeOverviewActions.addScopeTPCCommentSuccess, addScopeTPCCommentSuccessHandler),
  on(ScopeOverviewActions.addComponentTPCCommentSuccess, addComponentTPCCommentSuccessHandler),
  on(ScopeOverviewActions.addScopeCommentSuccess, addScopeCommentSuccessHandler),
  on(ScopeOverviewActions.getDeliverableAndComponentFees, getDeliverableAndComponentFeesHandler),
  on(ScopeOverviewActions.getDeliverableAndComponentFeesSuccess, getDeliverableAndComponentFeesSuccessHandler),
  on(ScopeOverviewActions.getDeliverableAndComponentFeesFail, getDeliverableAndComponentFeesFailHandler),
  on(ScopeOverviewActions.getAllDeliverableTPCs, getAllDeliverableTPCsHandler),
  on(ScopeOverviewActions.getAllDeliverableTPCsSuccess, getAllDeliverableTPCsSuccessHandler),
  on(ScopeOverviewActions.getAllDeliverableTPCsFail, getAllDeliverableTPCsFailHandler),
  on(ScopeOverviewActions.searchComponentsForScopeSuccess, searchComponentsForScopeSuccessHandler),
  on(ScopeOverviewActions.searchNonDistinctComponentsSuccess, searchNonDistinctComponentsSuccessHandler),
  on(ScopeOverviewActions.searchDepartmentsForScopeSuccess, searchDepartmentsForScopeSuccessHandler),
  on(ScopeOverviewActions.searchRolesForScopeSuccess, searchRolesForScopeSuccessHandler),
  on(ScopeOverviewActions.applyLocationToRoleSuccess, applyLocationToRoleSuccessHandler),
  on(ScopeOverviewActions.setDialogId, setDialogIdHandler),
  on(ScopeOverviewActions.applyLocationToRoleSuccess, applyLocationToRoleSuccessHandler),
  on(ScopeOverviewActions.getScenarioSuccess, getScenarioSuccessHandler),
  on(ScopeOverviewActions.updateScenario, updateScenarioHandler),
  on(ScopeOverviewActions.resetScenario, resetScenarioHandler),
  on(ScopeConfigurationActions.saveConfigurationSuccess, saveConfigurationSuccessHandler),
  on(ScopeConfigurationActions.updateConfiguration, updateConfigurationHandler),
  on(ScopePaginatedOverviewActions.getCurrentScope, getCurrentScopeHandler),
  on(ScopePaginatedOverviewActions.getCurrentScopeSuccess, getCurrentScopeSuccessHandler),
  on(ScopeOverviewActions.getCurrentScopeSuccess, getCurrentScopeByRoleSuccessHandler),
  on(
    ScopePaginatedOverviewActions.getCurrentScopeFail,
    ScopeOverviewActions.getCurrentScopeFail,
    getCurrentScopeFailHandler
  ),
  on(ScopePaginatedOverviewActions.getCurrentScopeValues, getCurrentScopeValuesHandler),
  on(ScopePaginatedOverviewActions.getCurrentScopeValuesFail, getCurrentScopeValuesFailHandler),
  on(ScopePaginatedOverviewActions.getCurrentScopeValuesSuccess, getCurrentScopeValuesSuccessHandler),
  on(ScopePaginatedOverviewActions.requestScopeDeliverables, getScopeDeliverablesLoadingHandler(true)),
  on(ScopePaginatedOverviewActions.getScopeDeliverablesFail, getScopeDeliverablesLoadingHandler(false)),
  on(ScopePaginatedOverviewActions.getScopeDeliverablesSuccess, getScopeDeliverablesSuccessHandler),
  on(ScopePaginatedOverviewActions.showNextBufferedDeliverables, showNextBufferedDeliverablesHandler),
  on(ScopePaginatedOverviewActions.requestScopeSections, getScopeSectionsLoadingHandler(true)),
  on(ScopePaginatedOverviewActions.getScopeSectionsFail, getScopeSectionsLoadingHandler(false)),
  on(ScopePaginatedOverviewActions.getScopeSectionsSuccess, getScopeSectionsSuccessHandler),
  on(ScopePaginatedOverviewActions.requestScopeSectionDeliverables, getScopeSectionDeliverablesLoadingHandler(true)),
  on(ScopePaginatedOverviewActions.getScopeSectionDeliverablesFail, getScopeSectionDeliverablesLoadingHandler(false)),
  on(ScopePaginatedOverviewActions.getScopeSectionDeliverablesSuccess, getScopeSectionDeliverablesSuccessHandler),
  on(ScopePaginatedOverviewActions.getCurrentScopeFail, getCurrentScopeFailHandler),
  on(ScopePaginatedOverviewActions.getScopeDeliverable, getScopeDeliverable),
  on(ScopePaginatedOverviewActions.getUpdatedDeliverable, getUpdatedDeliverable),
  on(ScopePaginatedOverviewActions.getScopeDeliverableSuccess, getScopeDeliverableSuccessHandler),
  on(ScopePaginatedOverviewActions.getScopeDeliverableFail, getScopeDeliverableFailHandler),
  on(ScopePaginatedOverviewActions.moveDeliverable, moveDeliverableHandler),
  on(ScopePaginatedOverviewActions.moveDeliverableSuccess, moveDeliverableSuccessHandler),
  on(ScopePaginatedOverviewActions.moveDeliverableFail, moveDeliverableFailHandler),
  on(ScopePaginatedOverviewActions.getScopeSection, getScopeSectionLoadingHandler(true)),
  on(ScopePaginatedOverviewActions.getScopeSectionFail, getScopeSectionLoadingHandler(false)),
  on(ScopePaginatedOverviewActions.getScopeSectionSuccess, getScopeSectionSuccessHandler)
)

export function scopeOverviewReducer(state: ScopeOverviewState | undefined, action: Action): ScopeOverviewState {
  return ScopeOverviewReducer(state, action)
}
